import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Grid, 
  Button, 
  ButtonGroup, 
  Container,
  TextField,
  InputAdornment,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  GlobalStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Menu,
  MenuItem as SelectMenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  TablePagination,
  Divider,
  Autocomplete
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import GetAppIcon from '@mui/icons-material/GetApp';
import AddIcon from '@mui/icons-material/Add';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import UndoIcon from '@mui/icons-material/Undo';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // ✅ YENİ: E-Fatura için ✓ ikonu
import { styled as muiStyled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getBranches as getBranchesAdapter, getBranchFirmInfo } from '../../../services/migration/branchDataService';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import { ACCOUNTING_API, EINVOICE_API, handleApiError } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import InvoiceDisplayModal from './InvoiceDisplayModal';
import type { InvoiceData } from './InvoiceDisplayModal';
import CompanyDetailsModal from './CompanyDetailsModal';
import InvoiceDeleteConfirmModal from './InvoiceDeleteConfirmModal';
import EInvoiceSendModal from './EInvoiceSendModal';
import EInvoiceStatusModal from './EInvoiceStatusModal';
import EInvoiceDetailModal from '../e-invoice/EInvoiceDetailModal';
import BusinessIcon from '@mui/icons-material/Business';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import AccountingDataTable, { DATATABLE_STYLES } from '../shared/AccountingDataTable';
import ProjectBadge from '../shared/ProjectBadge';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import RecordHistoryModal from '../../common/RecordHistoryModal';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';

// Başlık alanı için styled component - sadece web için
const HeaderContainer = muiStyled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Yeni Sabitler
const TRANSACTION_TYPES = {
  SALES: 'sales', // Mevcut tipler korunmalı
  PURCHASE: 'purchase', // Mevcut tipler korunmalı
  QUICK_SALE: 'quick_sale' // Yeni Hızlı Satış Tipi
};

const INVOICE_TYPE_NAMES: { [key: string]: string } = {
  [TRANSACTION_TYPES.SALES]: 'Satış Faturası',
  [TRANSACTION_TYPES.PURCHASE]: 'Alış Faturası',
  [TRANSACTION_TYPES.QUICK_SALE]: 'Hızlı Satış (Kapalı)',
  // Backend'den gelen büyük harfli değerler için
  'SALES': 'Satış Faturası',
  'PURCHASE': 'Alış Faturası',
  'QUICK_SALE': 'Hızlı Satış (Kapalı)'
};

// ✅ YENİ: E-Fatura Profil Türleri (Backend'den hem TEMEL hem TEMELFATURA gelebilir)
const EINVOICE_PROFILE_TYPES = [
  { value: 'TEMEL', label: 'Temel Fatura', aliases: ['TEMEL', 'TEMELFATURA'] },
  { value: 'TICARI', label: 'Ticari Fatura', aliases: ['TICARI', 'TICARIFATURA'] },
  { value: 'KAMU', label: 'Kamu', aliases: ['KAMU'] },
  { value: 'IHRACAT', label: 'İhracat', aliases: ['IHRACAT'] },
  { value: 'YOLCUBERABER', label: 'Yolcu Beraber', aliases: ['YOLCUBERABER'] },
  { value: 'HALTIPI', label: 'Hal Tipi', aliases: ['HALTIPI'] },
  { value: 'ENERJI', label: 'Enerji', aliases: ['ENERJI'] },
  { value: 'ILAC', label: 'İlaç', aliases: ['ILAC'] },
  { value: 'TIBBICIHAZ', label: 'Tıbbi Cihaz', aliases: ['TIBBICIHAZ'] },
  { value: 'YATIRIMTESVIK', label: 'Yatırım Teşvik', aliases: ['YATIRIMTESVIK'] },
];

// Invoice arayüzünü, InvoiceDisplayModal'ın InvoiceData'sı ile birleştirerek tanımla
interface Invoice extends InvoiceData { // InvoiceData'dan miras al
  // Invoice.tsx'e özgü veya InvoiceData'da olmayan ama burada kullanılan alanlar:
  accountId: string; 
  // PYS: bağlı olduğu proje (bağ yoksa null)
  projectId?: number | null;
  projectName?: string | null;
  createdAt: string;
  branchId: string | null; 
  status: 'draft' | 'completed' | 'canceled';
  exchangeRate: number;
  transactionType?: string;
  originalInvoiceTotal?: number; // ✅ YENİ: Orijinal fatura tutarı
  eInvoiceETTN?: string;
  eInvoiceBelgeOid?: string;
  /** Entegratörün seriden ürettiği resmi e-fatura numarası (ör. ABC2026000000123) */
  eInvoiceOfficialNumber?: string;
  eInvoiceStatus?: string;
  eInvoiceProfileType?: string;
  eInvoiceTypeCode?: string; // SATIS, IADE, TEVKIFAT, TEVKIFATIADE ...
  eInvoiceProvider?: string; // "finansbank", "pavopay" vb.
  returnedInvoiceId?: string;
  returnedInvoiceNumber?: string;
  returnedInvoiceDate?: string;
  // InvoiceData'da zaten olan ama burada da kullanılan alanlar (InvoiceData'dan miras alındığı için tekrar tanımlamaya gerek yok):
  // id: string;
  // invoiceNumber: string;
  // account: { companyName: string; taxNumber?: string; taxOffice?: string; address?: string; phone?: string; email?: string; };
  // date: string;
  // dueDate?: string;
  // items: any[]; // InvoiceData'da items: InvoiceItem[] olarak daha spesifik. Bu uyum sağlanmalı.
  // notes?: string;
  // subtotal: number;
  // vatTotal: number;
  // sctTotal?: number;
  // discountTotal?: number;
  // grandTotal: number;
  // currency: string;
  // type: 'sales' | 'purchase' | 'quick_sale';
  // isClosed?: boolean;
  // description?: string;
  // branchInfo?: { name: string; taxNumber?: string; ... };
}

// Tablo sütunu arayüzü
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  description?: string;
}

// İlk olarak printStyles'ı ekleyelim - Yaklaşık satır 50 civarında, ScrollableContent tanımından sonra
const printStyles = `
  @media print {
    body > *:not(.MuiDialog-root),
    .MuiDialog-root > *:not(.MuiDialog-container),
    .MuiDialog-container > *:not(.MuiPaper-root),
    .MuiDialog-paper > *:not(.MuiDialogContent-root),
    .MuiDialogTitle-root,
    .MuiDialogActions-root {
      display: none !important;
    }

    .MuiDialog-root,
    .MuiDialog-container,
    .MuiDialog-paper,
    .MuiDialogContent-root {
      display: block !important; /* Ensure these are block for layout */
      width: auto !important;
      height: auto !important;
      overflow: visible !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    #printableInvoiceContent {
      width: 210mm !important; 
      min-height: 290mm !important; /* A4 yüksekliğine yakın bir değer, içeriğe göre ayarlanabilir */
      padding: 10mm !important; /* Fatura içi kenar boşlukları */
      margin: 0 auto !important; /* Sayfada ortala */
      box-shadow: none !important;
      border: none !important;
      background-color: white !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      font-size: 10pt; /* Yazdırma için okunabilir bir font boyutu */
    }

    @page {
      size: A4;
      margin: 0;
    }

    #printableInvoiceContent .MuiPaper-root { /* Fatura içindeki Paper componentleri */
      box-shadow: none !important;
      border: 1px solid #ccc !important;
      background-color: white !important;
    }

    #printableInvoiceContent table {
      width: 100% !important;
      page-break-inside: auto;
    }
    #printableInvoiceContent tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    #printableInvoiceContent thead {
      display: table-header-group;
    }
  }
`;

interface InvoicePageProps {
  isManager?: boolean;
  managerBranchId?: string | null;
}

// İşlem Geçmişi modalı alan adı Türkçeleştirmesi (backend Invoice/InvoiceItem property adları).
// Eşleşmeyen alanlar modalda ham adıyla gösterilir.
const INVOICE_HISTORY_FIELD_LABELS: Record<string, string> = {
  InvoiceNumber: 'Fatura No',
  InvoiceType: 'Fatura Tipi',
  ProjectId: 'Proje',
  InvoiceDate: 'Fatura Tarihi',
  DueDate: 'Vade Tarihi',
  AccountId: 'Cari Hesap',
  AccountName: 'Cari Adı',
  AccountTaxNumber: 'Vergi No',
  AccountTaxOffice: 'Vergi Dairesi',
  AccountAddress: 'Cari Adresi',
  AccountPhone: 'Cari Telefonu',
  AccountEmail: 'Cari E-postası',
  DespatchDocumentNumber: 'İrsaliye No',
  DespatchDocumentDate: 'İrsaliye Tarihi',
  Subtotal: 'Ara Toplam',
  VatTotal: 'KDV Toplamı',
  SctTotal: 'ÖTV Toplamı',
  DiscountTotal: 'İndirim Toplamı',
  GrandTotal: 'Genel Toplam',
  TevkifatRate: 'Tevkifat Oranı',
  TevkifatAmount: 'Tevkifat Tutarı',
  StopajRate: 'Stopaj Oranı',
  StopajAmount: 'Stopaj Tutarı',
  Currency: 'Para Birimi',
  ExchangeRate: 'Döviz Kuru',
  DiscountType: 'İndirim Giriş Modu',
  Notes: 'Notlar',
  Status: 'Durum',
  BranchId: 'Şube',
  PaymentType: 'Ödeme Tipi',
  PaymentMethod: 'Ödeme Şekli',
  Iban: 'IBAN',
  PaymentNote: 'Ödeme Notu',
  Description: 'Açıklama',
  IsClosed: 'Kapalı mı',
  EInvoiceStatus: 'E-Fatura Durumu',
  IsDeleted: 'Silindi mi',
  // InvoiceItem (fatura kalemi) alanları
  ItemName: 'Ürün/Hizmet Adı',
  Quantity: 'Miktar',
  Unit: 'Birim',
  UnitPrice: 'Birim Fiyat',
  EnteredPrice: 'Girilen Fiyat',
  PriceType: 'Fiyat Tipi',
  LineTotal: 'Satır Toplamı',
  VatRate: 'KDV Oranı',
  VatAmount: 'KDV Tutarı',
  SctRate: 'ÖTV Oranı',
  SctAmount: 'ÖTV Tutarı',
  DiscountRate: 'İndirim Oranı',
  DiscountAmount: 'İndirim Tutarı',
  NetTotal: 'Net Toplam',
  GrossTotal: 'Brüt Toplam',
  WarehouseId: 'Depo',
  LineNumber: 'Satır No',
  IsManualItem: 'Stoksuz Ürün mü',
};

const Invoice: React.FC<InvoicePageProps> = ({ isManager = false, managerBranchId }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const decodeMojibake = (value?: string | null): string => {
    if (!value) return '';
    let decoded = value;
    for (let i = 0; i < 2; i += 1) {
      if (!/[ÃÂÅÄ]/.test(decoded)) break;
      try {
        const next = decodeURIComponent(escape(decoded));
        if (!next || next === decoded) break;
        decoded = next;
      } catch {
        break;
      }
    }
    return decoded;
  };
  
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  // İstatistik hesaplamaları (mevcut verilerden)
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // İstatistik kartları - mevcut sayfadaki verilere göre hesaplanır
  const invoiceStats = useMemo(() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const salesThisMonth = invoices.filter(inv =>
      (inv.type === 'sales' || inv.type === 'quick_sale') &&
      inv.status !== 'canceled' &&
      new Date(inv.createdAt || inv.date) >= thisMonthStart
    );
    const salesCount = salesThisMonth.length;
    const salesTotal = salesThisMonth.reduce((sum, inv) => sum + inv.grandTotal, 0);

    const purchaseThisMonth = invoices.filter(inv =>
      inv.type === 'purchase' &&
      inv.status !== 'canceled' &&
      new Date(inv.createdAt || inv.date) >= thisMonthStart
    );
    const purchaseCount = purchaseThisMonth.length;
    const purchaseTotal = purchaseThisMonth.reduce((sum, inv) => sum + inv.grandTotal, 0);

    return { salesCount, salesTotal, purchaseCount, purchaseTotal };
  }, [invoices]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyBranchInfo, setCompanyBranchInfo] = useState<InvoiceData['branchInfo'] | null>(null);
  const [branchMap, setBranchMap] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [hasEInvoiceIntegration, setHasEInvoiceIntegration] = useState(false);
  // Aktif e-fatura entegratörü ("finansbank" | "edm" | "pavopay"). İçe aktarılan
  // faturalarda provider DB'ye yazılmamışsa detay görüntülemede fallback olarak kullanılır.
  const [activeEInvoiceProvider, setActiveEInvoiceProvider] = useState<string | null>(null);
  // EDM TEST ortamında mı? (Edm:UseProduction=false). Yalnızca test ortamında açılacak
  // (henüz canlıya alınmamış) özellikler için gate. Canlıda (production) false olur ve gizlenir.
  const [edmIsTestMode, setEdmIsTestMode] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  
  // ✅ YENİ: Filtre state'leri
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<'all' | 'sales' | 'purchase'>('all');
  const [eInvoiceProfileFilter, setEInvoiceProfileFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>(isManager && managerBranchId ? managerBranchId : 'all');
  
  // Manager sube degistiginde filtreyi guncelle
  useEffect(() => {
    if (isManager && managerBranchId) {
      setBranchFilter(managerBranchId);
    }
  }, [isManager, managerBranchId]);

  // ✅ SERVER-SIDE PAGINATION state'leri
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const initialLoadDoneRef = React.useRef(false); // İlk yükleme tamamlandı mı?
  
  // Fatura Görüntüleme Modal state'leri
  const [isDisplayModalOpen, setIsDisplayModalOpen] = useState(false);
  const [selectedInvoiceForDisplay, setSelectedInvoiceForDisplay] = useState<Invoice | null>(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);

  // E-Fatura Detay Modal state'leri
  const [isEInvoiceDetailModalOpen, setIsEInvoiceDetailModalOpen] = useState(false);
  const [selectedEInvoiceETTN, setSelectedEInvoiceETTN] = useState<string | null>(null);
  const [selectedEInvoiceNumber, setSelectedEInvoiceNumber] = useState<string | null>(null);
  const [selectedEInvoiceType, setSelectedEInvoiceType] = useState<'sales' | 'purchase' | 'quick_sale' | null>(null); // ✅ YENİ: Fatura tipi

  // PavoPay Fatura Görüntüleme state'leri
  const [pavoPayViewerOpen, setPavoPayViewerOpen] = useState(false);
  const [pavoPayInvoiceHtml, setPavoPayInvoiceHtml] = useState<string>('');
  const [pavoPayViewerLoading, setPavoPayViewerLoading] = useState(false);
  const [pavoPayViewerInvoiceRef, setPavoPayViewerInvoiceRef] = useState<string>('');

  // EDM Fatura Görüntüleme state'leri (kesilen e-fatura/e-arşiv HTML belgesi)
  const [edmViewerOpen, setEdmViewerOpen] = useState(false);
  const [edmInvoiceHtml, setEdmInvoiceHtml] = useState<string>('');
  const [edmViewerLoading, setEdmViewerLoading] = useState(false);
  const [edmViewerError, setEdmViewerError] = useState<string | null>(null);
  const [edmViewerInvoiceRef, setEdmViewerInvoiceRef] = useState<string>('');
  const [edmViewerInvoice, setEdmViewerInvoice] = useState<Invoice | null>(null);
  const [edmDownloading, setEdmDownloading] = useState<string | null>(null); // 'HTML' | 'XML' | 'PDF' | null
  const edmViewerIframeRef = React.useRef<HTMLIFrameElement>(null);

  // PavoPay sube eslestirme haritasi (Firebase branchId -> PavoPay BranchId int)
  const [pavoPayBranchMap, setPavoPayBranchMap] = useState<Record<string, string>>({});
  const [hasGlobalPavoPayIntegration, setHasGlobalPavoPayIntegration] = useState(false);
  const [pavoPayBranchWarningOpen, setPavoPayBranchWarningOpen] = useState(false);

  // Firma Bilgileri Modal state'i
  const [isCompanyDetailsModalOpen, setIsCompanyDetailsModalOpen] = useState(false);
  
  // E-Fatura Uyarı Modal state'i
  const [showEInvoiceModal, setShowEInvoiceModal] = useState(false);
  
  // Fatura Silme Onay Modal state'leri
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedInvoiceForDelete, setSelectedInvoiceForDelete] = useState<Invoice | null>(null);
  
  // E-Fatura Gönder Modal state'leri
  const [isEInvoiceSendModalOpen, setIsEInvoiceSendModalOpen] = useState(false);
  const [selectedInvoiceForEInvoice, setSelectedInvoiceForEInvoice] = useState<Invoice | null>(null);
  
  // E-Fatura Durum Sorgula Modal state'leri
  const [isEInvoiceStatusModalOpen, setIsEInvoiceStatusModalOpen] = useState(false);
  const [selectedInvoiceForStatusQuery, setSelectedInvoiceForStatusQuery] = useState<Invoice | null>(null);
  
  // Tablo sütunları - başlangıç değeri (E-Fatura sütunu entegrasyona göre dinamik eklenecek)
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([
    { id: 'rowNumber', label: tt('Sıra', 'No'), field: 'rowNumber', visible: true, fixed: true },
    { id: 'companyName', label: tt('Cari Hesap', 'Account'), field: 'account.companyName', visible: true, fixed: true },
    { id: 'date', label: tt('Fatura Tarihi', 'Invoice Date'), field: 'date', visible: true, fixed: false },
    { id: 'type', label: tt('Tür', 'Type'), field: 'type', visible: true, fixed: false },
    { id: 'eInvoiceProfileType', label: tt('Fatura Türü', 'Invoice Profile'), field: 'eInvoiceProfileType', visible: true, fixed: false },
    { id: 'invoiceNumber', label: tt('Fatura No', 'Invoice No'), field: 'invoiceNumber', visible: true, fixed: false },
    { id: 'sourceOfferNumber', label: tt('Teklif No', 'Offer No'), field: 'sourceOfferNumber', visible: true, fixed: false },
    { id: 'dueDate', label: tt('Vade Tarihi', 'Due Date'), field: 'dueDate', visible: true, fixed: false },
    { id: 'currency', label: tt('Para Birimi', 'Currency'), field: 'currency', visible: true, fixed: false },
    { id: 'branchId', label: tt('Şube', 'Branch'), field: 'branchId', visible: true, fixed: false },
    { id: 'grandTotal', label: tt('Toplam Tutar', 'Grand Total'), field: 'grandTotal', visible: true, fixed: true },
    { id: 'status', label: tt('Durum', 'Status'), field: 'status', visible: false, fixed: false },
    { id: 'actions', label: tt('İşlemler', 'Actions'), field: 'actions', visible: true, fixed: true },
  ]);

  useEffect(() => {
    setTableColumns(prev => prev.map(col => {
      const localizedLabels: Record<string, string> = {
        rowNumber: tt('Sıra', 'No'),
        companyName: tt('Cari Hesap', 'Account'),
        date: tt('Fatura Tarihi', 'Invoice Date'),
        type: tt('Tür', 'Type'),
        eInvoiceProfileType: tt('Fatura Türü', 'Invoice Profile'),
        invoiceNumber: tt('Fatura No', 'Invoice No'),
        sourceOfferNumber: tt('Teklif No', 'Offer No'),
        dueDate: tt('Vade Tarihi', 'Due Date'),
        currency: tt('Para Birimi', 'Currency'),
        branchId: tt('Şube', 'Branch'),
        grandTotal: tt('Toplam Tutar', 'Grand Total'),
        status: tt('Durum', 'Status'),
        actions: tt('İşlemler', 'Actions'),
        eInvoiceStatus: tt('E-Fatura', 'E-Invoice'),
      };
      return { ...col, label: localizedLabels[col.id] ?? col.label };
    }));
  }, [isTr]);
  
  // ✅ YENİ: E-fatura entegrasyonu varsa "E-Fatura" sütununu dinamik olarak ekle
  useEffect(() => {
    if (hasEInvoiceIntegration) {
      setTableColumns(prev => {
        // Zaten varsa ekleme
        if (prev.some(col => col.id === 'eInvoiceStatus')) {
          return prev;
        }
        
        // "grandTotal" sütunundan önce ekle
        const grandTotalIndex = prev.findIndex(col => col.id === 'grandTotal');
        const newColumns = [...prev];
        newColumns.splice(grandTotalIndex, 0, { 
          id: 'eInvoiceStatus', 
          label: tt('E-Fatura', 'E-Invoice'), 
          field: 'eInvoiceETTN', 
          visible: true, 
          fixed: false 
        });
        return newColumns;
      });
    } else {
      // Entegrasyon yoksa sütunu kaldır
      setTableColumns(prev => prev.filter(col => col.id !== 'eInvoiceStatus'));
    }
  }, [hasEInvoiceIntegration, isTr]);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25); // ✅ TABLE_STYLES.rowsPerPageOptions ile uyumlu [25, 50, 100]
  
  // Mobil için aksiyon menüsü (fatura satırları)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedInvoice(null);
  };

  // İşlem Geçmişi modalı — handleCloseMenu selectedInvoice'ı sıfırladığı için
  // fatura bilgisi ayrı state'te saklanır.
  const [historyModalInvoice, setHistoryModalInvoice] = useState<{ id: string; label: string } | null>(null);
  
  // Üst menü için 3 nokta menüsü
  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  
  const handleOpenMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
  };
  
  const handleCloseMoreMenu = () => {
    setAnchorElMore(null);
  };
  
  // Raporlar sayfasına yönlendir
  const handleOpenReports = () => {
    handleCloseMoreMenu();
    navigate('/fatura/raporlar');
  };
  
  // E-fatura entegrasyonunu kontrol et (Finansbank/EDM veya PavoPay)
  const checkEInvoiceIntegration = async (companyId: string) => {
    try {
      console.log('🔍 [E-FATURA] Entegrasyon kontrolü yapılıyor...', { companyId });
      
      let hasFinansbank = false;
      let hasPavoPay = false;

      try {
        const response = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
        const integ = response.data?.data;
        hasFinansbank = !!integ;
        // Aktif entegratör (EDM / finansbank) — fallback için sakla
        const prov = (integ?.providerType || integ?.ProviderType || '').toString().toLowerCase();
        if (prov) setActiveEInvoiceProvider(prov);

        // EDM ise ortam bilgisini (test/canlı) çek — yalnızca testte açılacak özellikler için.
        if (prov === 'edm') {
          try {
            const envResp = await einvoiceApi.get(EINVOICE_API.EDM_ENVIRONMENT);
            setEdmIsTestMode(envResp.data?.success === true && envResp.data?.isTestMode === true);
          } catch {
            setEdmIsTestMode(false);
          }
        } else {
          setEdmIsTestMode(false);
        }
      } catch { /* 404 = yok */ }

      try {
        const ppResponse = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
        const ppList = ppResponse.data?.data || ppResponse.data || [];
        hasPavoPay = Array.isArray(ppList) && ppList.length > 0;
        if (hasPavoPay && !activeEInvoiceProvider) setActiveEInvoiceProvider('pavopay');
      } catch { /* 404 = yok */ }

      const hasIntegration = hasFinansbank || hasPavoPay;
      console.log('✅ [E-FATURA] Entegrasyon durumu:', { hasFinansbank, hasPavoPay, hasIntegration });
      setHasEInvoiceIntegration(hasIntegration);
    } catch (err) {
      console.error('❌ [E-FATURA] Entegrasyon kontrolü hatası:', err);
      setHasEInvoiceIntegration(false);
    }
  };

  // Firma bilgilerini yükleme fonksiyonu (tekrar kullanılabilir hale getirildi)
  const loadCompanyDetails = async (userId: string) => {
    try {
      const userData = await firebaseDataService.getUserData(userId);
      if (!userData) {
        console.error('loadCompanyDetails: Kullanıcı bilgileri bulunamadı.');
        return null; // veya hata fırlat
      }
      const currentCompanyId = userData.companyId;
      if (!currentCompanyId) {
        console.error('loadCompanyDetails: Şirket ID bulunamadı.');
        return null;
      }
      setCompanyId(currentCompanyId);

      // Manager modunda sube firma bilgilerini oku (SQL-once + HybridFallback Firebase yedegi).
      if (isManager && managerBranchId) {
        try {
          const bd = await getBranchFirmInfo(currentCompanyId, managerBranchId);
          if (bd && (bd.firmName || bd.firmTaxNumber)) {
            setCompanyBranchInfo({
              name: bd.firmName || 'Şube Firma Adı Belirtilmemiş',
              taxNumber: bd.firmTaxNumber,
              taxOffice: bd.firmTaxOffice,
              address: bd.firmAddress,
              phone: bd.firmPhone,
              email: bd.firmEmail,
              mersisNo: bd.firmMersisNo,
              tradeRegistryNo: bd.firmTradeRegistryNo,
              // Sube-seviyesi bankAccounts SQL'de modellenmiyor (modal da yazmiyor) → bos.
              bankAccounts: [],
            });
            return currentCompanyId;
          }
        } catch (brErr) {
          console.warn('Şube firma bilgileri yüklenemedi, ana firma bilgilerini deniyorum:', brErr);
        }
      }

      const companyData = await getCompanyInfo(currentCompanyId);
      if (companyData) {
        const branchInfoData = {
          name: companyData.companyName || 'Firma Adı Belirtilmemiş',
          taxNumber: companyData.taxNumber,
          taxOffice: companyData.taxOffice,
          address: companyData.address,
          phone: companyData.phone,
          email: companyData.email,
          mersisNo: companyData.mersisNo,
          tradeRegistryNo: companyData.tradeRegistryNo,
          bankAccounts: companyData.bankAccounts || [],
        };
        setCompanyBranchInfo(branchInfoData);
        return currentCompanyId;
      } else {
        setCompanyBranchInfo({ 
          name: 'Firma Bilgileri Yüklenemedi', 
          taxNumber: '-', taxOffice: '-', address: '-', 
          phone: '-', email: '-', mersisNo: '-', 
          tradeRegistryNo: '-', bankAccounts: [] 
         });
        return currentCompanyId;
      }
    } catch (err) {
        console.error('loadCompanyDetails: Firma bilgileri yüklenirken hata:', err);
        setError('Firma bilgileri yüklenirken bir hata oluştu.');
        setCompanyBranchInfo({ name: 'Hata oluştu' });
        return null;
    }
  };
  
  // Şirket ID'sini ve faturaları yükleme
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError('Kullanıcı oturumu açık değil.');
        setLoading(false);
        return;
      }

      const loadedCompanyId = await loadCompanyDetails(uid);

      if (loadedCompanyId) {
        // ✅ YENİ: E-fatura entegrasyonunu kontrol et
        await checkEInvoiceIntegration(loadedCompanyId);
        loadAllInvoices(loadedCompanyId);
      } else {
        setError('Şirket bilgileri yüklenemedi, faturalar listelenemiyor.')
        setLoading(false);
      }
    };
    
    loadInitialData();
    
    // ✅ YENİ: SQL kullanıyoruz, Firebase listener cleanup'ına gerek yok
  }, []);

  useEffect(() => {
    if (!companyId) return;
    const loadBranches = async () => {
      try {
        const serviceBranches = await getBranchesAdapter(companyId);
        const map: Record<string, string> = {};
        serviceBranches.forEach((b) => {
          map[b.id] = b.name || 'İsimsiz Şube';
        });
        setBranchMap(map);
      } catch (err) {
        console.warn('Şube bilgileri yüklenemedi:', err);
      }
    };
    loadBranches();
  }, [companyId]);

  // PavoPay entegrasyonlarindan sube-BranchId eslestirme haritasi olustur
  useEffect(() => {
    if (!companyId) return;
    const loadPavoPayMap = async () => {
      try {
        const response = await accountingApi.get(ACCOUNTING_API.PAVOPAY_INTEGRATION_LIST);
        const list = response.data?.data || response.data?.Data || [];
        const map: Record<string, string> = {};
        let foundGlobal = false;
        list.forEach((item: any) => {
          const bId = item.branchId ?? item.BranchId;
          if (!bId) {
            foundGlobal = true;
            return;
          }
          map[bId] = bId;
        });
        console.log('🔍 [PAVOPAY MAP] result:', { foundGlobal, map });
        setPavoPayBranchMap(map);
        setHasGlobalPavoPayIntegration(foundGlobal);
      } catch (err) {
        console.error('PavoPay entegrasyonları yüklenemedi:', err);
      }
    };
    loadPavoPayMap();
  }, [companyId, branchMap]);

  // Secili subenin PavoPay BranchId'sini hesapla
  const activePavoPayBranchId = useMemo(() => {
    if (branchFilter === 'all' || branchFilter === 'none') return undefined;
    return pavoPayBranchMap[branchFilter] || undefined;
  }, [branchFilter, pavoPayBranchMap]);
  
  // ✅ SERVER-SIDE PAGINATION: Faturaları sayfalanmış olarak yükle
  const loadInvoicesPage = async (
    targetCompanyId: string,
    targetPage: number = 1,
    targetPageSize: number = rowsPerPage,
    targetSearch?: string,
    targetInvoiceType?: string,
    targetEInvoiceProfile?: string,
    targetSortColumn?: string,
    targetSortDirection?: string,
    targetBranchId?: string
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`📋 [SQL] Faturalar yükleniyor (Sayfa ${targetPage})...`, targetCompanyId);
      
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_COMPANY({
        page: targetPage,
        pageSize: targetPageSize,
        search: targetSearch || undefined,
        invoiceType: targetInvoiceType || undefined,
        eInvoiceProfileType: targetEInvoiceProfile || undefined,
        sortColumn: targetSortColumn || undefined,
        sortDirection: targetSortDirection || undefined,
        branchId: targetBranchId || undefined,
      }));
      
      const result = response.data;
      const sqlInvoices = result.data || [];
      const serverTotalCount = result.totalCount || 0;
      const serverTotalPages = result.totalPages || 0;
      
      console.log(`✅ [SQL] ${sqlInvoices.length}/${serverTotalCount} fatura yüklendi (Sayfa ${targetPage}/${serverTotalPages})`);
      
      // SQL formatından frontend formatına dönüştür
      const invoicesList: Invoice[] = sqlInvoices.map((data: any) => {
        const itemsForDisplay = (data.items || []).map((item: any) => ({
          itemName: item.itemName || 'Bilinmeyen Ürün',
          quantity: item.quantity || 0,
          unit: item.unit || 'Adet',
          unitPrice: item.unitPrice || 0,
          vatRate: item.vatRate || 0,
          sctRate: item.sctRate || 0,
          discountRate: item.discountRate || 0,
          lineTotal: item.lineTotal || 0,
          vatAmount: item.vatAmount || 0,
          grossTotal: item.grossTotal || 0,
        }));
        
        return {
          id: data.id,
          invoiceNumber: data.invoiceNumber || '',
          accountId: data.accountId || '',
          projectId: data.projectId ?? null,
          projectName: data.projectName || null,
          account: {
            companyName: decodeMojibake(data.account?.companyName || data.accountName || ''),
            taxNumber: data.accountTaxNumber || '',
            taxOffice: data.accountTaxOffice || '',
            address: data.accountAddress || '',
            phone: data.accountPhone || '',
            email: data.accountEmail || '',
          },
          date: data.invoiceDate ? new Date(data.invoiceDate).toISOString().split('T')[0] : '',
          dueDate: data.dueDate ? new Date(data.dueDate).toISOString().split('T')[0] : '',
          items: itemsForDisplay,
          notes: data.notes || '',
          subtotal: data.subtotal || 0,
          vatTotal: data.vatTotal || 0,
          sctTotal: data.sctTotal || 0,
          discountTotal: data.discountTotal || 0,
          grandTotal: data.grandTotal || 0,
          tevkifatAmount: data.tevkifatAmount ?? data.TevkifatAmount ?? 0,
          stopajAmount: data.stopajAmount ?? data.StopajAmount ?? 0,
          tevkifatRate: data.tevkifatRate ?? data.TevkifatRate ?? undefined,
          stopajRate: data.stopajRate ?? data.StopajRate ?? undefined,
          originalInvoiceTotal: data.originalInvoiceTotal !== null && data.originalInvoiceTotal !== undefined ? data.originalInvoiceTotal : undefined,
          eInvoiceETTN: data.eInvoiceETTN || data.EInvoiceETTN || undefined,
          eInvoiceBelgeOid: data.eInvoiceBelgeOid || data.EInvoiceBelgeOid || undefined,
          eInvoiceOfficialNumber: data.eInvoiceOfficialNumber || data.EInvoiceOfficialNumber || undefined,
          eInvoiceStatus: data.eInvoiceStatus || data.EInvoiceStatus || undefined,
          eInvoiceProfileType: data.eInvoiceProfileType || data.EInvoiceProfileType || undefined,
          eInvoiceTypeCode: data.eInvoiceTypeCode || data.EInvoiceTypeCode || undefined,
          eInvoiceProvider: data.eInvoiceProvider || data.EInvoiceProvider || undefined,
          returnedInvoiceId: data.returnedInvoiceId || data.ReturnedInvoiceId || undefined,
          returnedInvoiceNumber: data.returnedInvoiceNumber || data.ReturnedInvoiceNumber || undefined,
          returnedInvoiceDate: data.returnedInvoiceDate || data.ReturnedInvoiceDate || undefined,
          sourceOfferId: data.sourceOfferId || data.SourceOfferId || undefined,
          sourceOfferNumber: data.sourceOfferNumber || data.SourceOfferNumber || undefined,
          createdAt: data.createdAt || '',
          branchId: data.branchId || null,
          status: data.status || 'completed',
          currency: data.currency || 'TRY',
          exchangeRate: data.exchangeRate || 1,
          type: data.invoiceType === 'PURCHASE' ? 'purchase' : data.invoiceType === 'QUICK_SALE' ? 'quick_sale' : 'sales',
          transactionType: data.invoiceType,
          isClosed: data.isClosed || false,
          description: data.description || '',
          branchInfo: companyBranchInfo
        } as Invoice;
      });
      
      setInvoices(invoicesList);
      setTotalCount(serverTotalCount);
      setTotalPages(serverTotalPages);
      setLoading(false);
      
    } catch (error: any) {
      console.error('❌ [SQL] Faturalar yüklenirken hata:', error);
      setError('Faturalar yüklenirken bir hata oluştu.');
      setLoading(false);
    }
  };
  
  // Eski fonksiyon adını koruyarak uyumluluk sağla (ilk yükleme için)
  const loadAllInvoices = async (targetCompanyId: string) => {
    await loadInvoicesPage(targetCompanyId, 1, rowsPerPage, searchTerm, invoiceTypeFilter, eInvoiceProfileFilter, undefined, undefined, branchFilter);
    initialLoadDoneRef.current = true; // İlk yükleme tamamlandı
  };

  // ✅ YENİ: URL'den invoiceId parametresi varsa o faturayı otomatik aç
  useEffect(() => {
    const invoiceIdFromUrl = searchParams.get('invoiceId');
    if (invoiceIdFromUrl && invoices.length > 0 && !loading) {
      const targetInvoice = invoices.find(inv => inv.id === invoiceIdFromUrl);
      if (targetInvoice) {
        console.log('📄 [URL] Fatura otomatik açılıyor:', invoiceIdFromUrl);
        handleViewInvoice(targetInvoice);
        searchParams.delete('invoiceId');
        setSearchParams(searchParams, { replace: true });
      } else {
        console.warn('⚠️ [URL] Fatura bulunamadı:', invoiceIdFromUrl);
      }
    }
  }, [invoices, loading, searchParams, setSearchParams]);
  
  const handleNewSalesInvoice = () => {
    // Satış faturası sayfasına yönlendir
    navigate('/fatura/satis/yeni');
  };

  const handleNewPurchaseInvoice = () => {
    navigate('/fatura/alis/yeni');
  };
  
  // ✅ SERVER-SIDE: Filtre veya arama değiştiğinde yeni API çağrısı yap
  useEffect(() => {
    if (!companyId) return;
    
    // İlk yükleme zaten loadAllInvoices'da yapıldı, tekrar çağırma
    if (!initialLoadDoneRef.current) return;
    
    // Arama terimi için debounce (500ms)
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    
    const timer = setTimeout(() => {
      setPage(0);
      loadInvoicesPage(companyId, 1, rowsPerPage, searchTerm, invoiceTypeFilter, eInvoiceProfileFilter, sortColumn, sortDirection, branchFilter);
    }, searchTerm ? 500 : 0);
    
    setSearchDebounceTimer(timer);
    
    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, invoiceTypeFilter, eInvoiceProfileFilter, branchFilter]);
  
  // Sayfa değişimi - Server-side
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    if (companyId) {
      loadInvoicesPage(companyId, newPage + 1, rowsPerPage, searchTerm, invoiceTypeFilter, eInvoiceProfileFilter, sortColumn, sortDirection, branchFilter);
    }
  };
  
  // Sayfa başına satır sayısı değişimi - Server-side
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (companyId) {
      loadInvoicesPage(companyId, 1, newRowsPerPage, searchTerm, invoiceTypeFilter, eInvoiceProfileFilter, sortColumn, sortDirection, branchFilter);
    }
  };
  
  // Server-side sıralama
  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortColumn(field);
    setSortDirection(direction);
    setPage(0);
    if (companyId) {
      loadInvoicesPage(companyId, 1, rowsPerPage, searchTerm, invoiceTypeFilter, eInvoiceProfileFilter, field, direction, branchFilter);
    }
  };
  
  // Durum metni
  const getStatusText = (status: string): string => {
    switch(status) {
      case 'completed': return tt('Tamamlandı', 'Completed');
      case 'draft': return tt('Taslak', 'Draft');
      case 'canceled': return tt('İptal Edildi', 'Canceled');
      default: return status;
    }
  };
  
  // Durum rengi
  const getStatusColor = (status: string): string => {
    switch(status) {
      case 'completed': return 'success.main';
      case 'draft': return 'warning.main';
      case 'canceled': return 'error.main';
      default: return 'text.primary';
    }
  };
  
  // Fatura türü metnini döndür (yeni fonksiyon)
  const getInvoiceDisplayType = (invoice: Invoice): string => {
    const typeKey = invoice.transactionType || invoice.type;
    if (typeKey === TRANSACTION_TYPES.QUICK_SALE) {
      return tt('Hızlı Satış (Kapalı)', 'Quick Sale (Closed)');
    }
    if (typeKey === TRANSACTION_TYPES.SALES || typeKey === 'SALES') return tt('Satış Faturası', 'Sales Invoice');
    if (typeKey === TRANSACTION_TYPES.PURCHASE || typeKey === 'PURCHASE') return tt('Alış Faturası', 'Purchase Invoice');
    return typeKey || tt('Diğer', 'Other');
  };
  
  // Fatura türü rengi (güncellendi)
  const getInvoiceTypeColor = (type: 'sales' | 'purchase' | 'quick_sale'): 'primary' | 'secondary' | 'info' => {
    if (type === TRANSACTION_TYPES.QUICK_SALE) return 'info';
    return type === 'sales' ? 'primary' : 'secondary';
  };

  // ✅ İADE: Fatura bir iade faturası mı? (tip kodu IADE/TEVKIFATIADE/YTBIADE veya orijinal referansı var)
  const isReturnInvoice = (invoice: Invoice): boolean => {
    const code = (invoice.eInvoiceTypeCode || '').toUpperCase();
    if (['IADE', 'TEVKIFATIADE', 'YTBIADE'].includes(code)) return true;
    return !!invoice.returnedInvoiceId;
  };

  // ✅ İADE: Yön bazlı etiket (Satıştan İade / Alıştan İade)
  const getReturnLabel = (invoice: Invoice): string => {
    const typeKey = invoice.transactionType || invoice.type;
    const isPurchase = typeKey === TRANSACTION_TYPES.PURCHASE || typeKey === 'PURCHASE' || typeKey === 'purchase';
    return isPurchase ? tt('Alıştan İade', 'Purchase Return') : tt('Satıştan İade', 'Sales Return');
  };
  
  // ✅ YENİ: E-Fatura profil türünü kullanıcı dostu formata çevir
  const getEInvoiceProfileTypeDisplay = (profileType?: string): string => {
    if (!profileType) return '-';
    
    const profileTypeMap: { [key: string]: string } = {
      'TEMELFATURA': tt('Temel Fatura', 'Basic Invoice'),
      'TEMEL': tt('Temel Fatura', 'Basic Invoice'),
      'TICARIFATURA': tt('Ticari Fatura', 'Commercial Invoice'),
      'TICARI': tt('Ticari Fatura', 'Commercial Invoice'),
      'IADE': tt('İade Faturası', 'Return Invoice'),
      'TEVKIFAT': tt('Tevkifat Faturası', 'Withholding Invoice'),
      'ISTISNA': tt('İstisna Faturası', 'Exemption Invoice'),
      'OZELMATRAH': tt('Özel Matrah Faturası', 'Special Base Invoice'),
      'IHRACAT': tt('İhracat Faturası', 'Export Invoice'),
      'YOLCUBERABER': tt('Yolcu Beraber Faturası', 'Traveler Accompanied Invoice'),
      'HASTANE': tt('Hastane Faturası', 'Hospital Invoice'),
      'OTV': tt('ÖTV Faturası', 'SCT Invoice'),
    };
    
    return profileTypeMap[profileType.toUpperCase()] || profileType;
  };
  
  // Tek faturayı kalemleriyle birlikte API'den çeken yardımcı fonksiyon
  const fetchInvoiceWithItems = async (invoice: Invoice): Promise<Invoice> => {
    if (!companyId) return invoice;
    
    try {
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoice.id));
      const result = response.data;
      const data = result.data || result;
      
      const itemsForDisplay = (data.items || []).map((item: any) => ({
        itemName: item.itemName || 'Bilinmeyen Ürün',
        quantity: item.quantity || 0,
        unit: item.unit || 'Adet',
        unitPrice: item.unitPrice || 0,
        vatRate: item.vatRate || 0,
        sctRate: item.sctRate || 0,
        discountRate: item.discountRate || 0,
        lineTotal: item.lineTotal || 0,
        vatAmount: item.vatAmount || 0,
        grossTotal: item.grossTotal || 0,
      }));
      
      console.log(`✅ [FATURA] Fatura detayı çekildi: ${data.invoiceNumber}, kalem sayısı: ${itemsForDisplay.length}, seller: ${data.sellerCompanyName || '-'} (${data.sellerSource || 'snapshot yok'})`);
      
      return {
        ...invoice,
        items: itemsForDisplay,
        // ✅ Seller snapshot — fatura kesilirken Invoices tablosuna yazılan satıcı bilgileri.
        // InvoiceDisplayModal franchise faturalarda doğru firma bilgisini göstermek için
        // bu alanları kullanır. Boşsa modal Firebase'deki global firma bilgisine fallback yapar.
        sellerCompanyName: data.sellerCompanyName ?? null,
        sellerTaxNumber: data.sellerTaxNumber ?? null,
        sellerTaxOffice: data.sellerTaxOffice ?? null,
        sellerAddress: data.sellerAddress ?? null,
        sellerPhone: data.sellerPhone ?? null,
        sellerEmail: data.sellerEmail ?? null,
        sellerWebsite: data.sellerWebsite ?? null,
        sellerMersisNo: data.sellerMersisNo ?? null,
        sellerTradeRegistryNo: data.sellerTradeRegistryNo ?? null,
        isFranchiseInvoice: data.isFranchiseInvoice ?? false,
        sellerSource: data.sellerSource ?? null,
        sellerBranchId: data.sellerBranchId ?? null,
      };
    } catch (error) {
      console.error('❌ [FATURA] Fatura detayı çekilirken hata:', error);
      return invoice;
    }
  };

  // Faturayı görüntüleme fonksiyonu güncellendi
  const handleViewInvoice = async (invoice: Invoice) => {
    console.log('📋 [FATURA] Fatura görüntüleme:', { 
      invoiceNumber: invoice.invoiceNumber, 
      type: invoice.type, 
      eInvoiceETTN: invoice.eInvoiceETTN,
      eInvoiceETTN_length: invoice.eInvoiceETTN?.length,
      eInvoiceETTN_type: typeof invoice.eInvoiceETTN,
      eInvoiceStatus: invoice.eInvoiceStatus,
      eInvoiceProfileType: invoice.eInvoiceProfileType,
      'ETTN var mı?': !!invoice.eInvoiceETTN,
      'ETTN boş mu?': invoice.eInvoiceETTN?.trim() === '',
      'isEInvoiceSent?': isEInvoiceSent(invoice)
    });
    
    // ⚠️ Gönderimi BAŞARISIZ (SEND - FAILED / ERROR) faturalarda entegratörde belge oluşmadığı için
    //     belge çekme denemesi boş döner. Bu durumda yerel fatura + kalemlerini gösteriyoruz.
    if (isEInvoiceSent(invoice) && !isEInvoiceFailed(invoice)) {
      // İçe aktarılan faturalarda provider DB'ye yazılmamış olabilir → aktif entegratöre düş
      const effectiveProvider = (invoice.eInvoiceProvider || activeEInvoiceProvider || '').toLowerCase();
      if (effectiveProvider === 'pavopay') {
        // Yeni gönderim yolu (einvoice API + PavoProvider) BelgeOid alanına Pavo'nun sayısal
        // InvoiceId'sini yazar → belge, provider-bağımsız /document endpoint'inden çekilebilir.
        // Eski POS cihaz akışında sadece ETTN vardır → Aktif Bank portal proxy'sine düşülür.
        const hasPavoNumericId = invoice.type !== 'purchase'
          && /^\d+$/.test((invoice.eInvoiceBelgeOid || '').trim());
        if (hasPavoNumericId) {
          console.log('📋 [FATURA] PavoPay faturası - einvoice API üzerinden görüntüleme:', {
            belgeOid: invoice.eInvoiceBelgeOid,
            invoiceNumber: invoice.invoiceNumber
          });
          handleViewEdmInvoice(invoice);
          return;
        }
        // PavoPay entegrasyonu hiç tanımlı değilse (ne şube ne global) uzaktan belge
        // çekilemez → yerel fatura detayına (kağıt fatura görünümü) düş.
        const hasAnyPavoPayIntegration = hasGlobalPavoPayIntegration || Object.keys(pavoPayBranchMap).length > 0;
        if (!hasAnyPavoPayIntegration) {
          console.log('📋 [FATURA] PavoPay entegrasyonu tanımlı değil - yerel fatura detayı gösteriliyor:', {
            invoiceNumber: invoice.invoiceNumber,
            ettn: invoice.eInvoiceETTN
          });
          setLoadingInvoiceDetail(true);
          setIsDisplayModalOpen(true);
          const fullInvoice = await fetchInvoiceWithItems(invoice);
          setSelectedInvoiceForDisplay(fullInvoice);
          setLoadingInvoiceDetail(false);
          return;
        }
        if (!activePavoPayBranchId && !hasGlobalPavoPayIntegration) {
          setPavoPayBranchWarningOpen(true);
          return;
        }
        console.log('📋 [FATURA] PavoPay faturası - Aktif Bank HTML görüntüleme (eski akış):', {
          ettn: invoice.eInvoiceETTN,
          invoiceNumber: invoice.invoiceNumber,
          provider: effectiveProvider
        });
        handleViewPavoPayInvoice(invoice);
      } else if (effectiveProvider === 'edm') {
        // EDM e-fatura/e-arşiv → belgeyi EDM'den çekip göster.
        // Alış (gelen) faturalar gelen kutusundan UUID ile; satış (giden) faturalar /document ile.
        console.log('📋 [FATURA] EDM faturası HTML görüntüleniyor:', {
          invoiceNumber: invoice.invoiceNumber,
          status: invoice.eInvoiceStatus,
          provider: effectiveProvider,
          type: invoice.type
        });
        handleViewEdmInvoice(invoice);
      } else {
        // QNB / Finansbank e-faturaları → EInvoiceDetailModal
        console.log('📋 [FATURA] E-fatura detayı (QNB) gösteriliyor:', { 
          ettn: invoice.eInvoiceETTN, 
          invoiceNumber: invoice.invoiceNumber,
          type: invoice.type,
          profileType: invoice.eInvoiceProfileType 
        });
        // @ts-ignore
        setSelectedEInvoiceETTN(invoice.eInvoiceETTN);
        setSelectedEInvoiceNumber(invoice.invoiceNumber);
        setSelectedEInvoiceType(invoice.type);
        setIsEInvoiceDetailModalOpen(true);
      }
    } else {
      // Normal fatura görüntüleme - kalemleri API'den çek
      console.log('📋 [FATURA] Normal fatura detayı gösteriliyor (ETTN YOK), kalemler çekiliyor...');
      setLoadingInvoiceDetail(true);
      setIsDisplayModalOpen(true);
      
      const fullInvoice = await fetchInvoiceWithItems(invoice);
      setSelectedInvoiceForDisplay(fullInvoice);
      setLoadingInvoiceDetail(false);
    }
  };

  const handleCloseDisplayModal = () => {
    setIsDisplayModalOpen(false);
    setSelectedInvoiceForDisplay(null);
  };

  const handleCloseEInvoiceDetailModal = () => {
    setIsEInvoiceDetailModalOpen(false);
    setSelectedEInvoiceETTN(null);
    setSelectedEInvoiceNumber(null);
    setSelectedEInvoiceType(null);
  };

  // PavoPay fatura HTML görüntüleme (Aktif Bank üzerinden)
  const handleViewPavoPayInvoice = async (invoice: Invoice) => {
    setPavoPayViewerOpen(true);
    setPavoPayViewerLoading(true);
    setPavoPayInvoiceHtml('');
    setPavoPayViewerInvoiceRef(invoice.invoiceNumber || invoice.eInvoiceETTN || '');

    try {
      const ettn = invoice.eInvoiceETTN;
      if (!ettn) throw new Error('ETTN bulunamadı');

      // Fatura tarihini kullanarak Aktif Bank'ta arama yap
      const invoiceDate = new Date(invoice.date);
      const startDate = new Date(invoiceDate);
      startDate.setDate(startDate.getDate() - 1);
      const endDate = new Date(invoiceDate);
      endDate.setDate(endDate.getDate() + 1);

      const qs = `sort=&pageSize=50&page=1&group=&filter=&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&filterStatus=1,2,3,4,5,6,7,8,9,12,17,23,24&taxId=&byIssueDate=true&isArchived=&profileType=&invoiceType=`;

      const searchResponse = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_PROXY, {
        Path: '/api/EInvoice/OutgoingInvoice/SearchInvoices',
        QueryString: qs,
        Method: 'GET',
        BranchId: activePavoPayBranchId
      });

      const aktifData = searchResponse.data;
      const items = aktifData?.Data || aktifData?.data || [];
      const matchedInvoice = items.find((item: any) =>
        (item.UUID || item.uuid) === ettn
      );

      if (!matchedInvoice) {
        throw new Error(`Aktif Bank üzerinde UUID=${ettn} ile eşleşen fatura bulunamadı.`);
      }

      const outgoingInvoiceId = matchedInvoice.OutgoingInvoiceId || matchedInvoice.IncomingInvoiceId || matchedInvoice.Id;
      console.log('📋 [PavoPay] Eşleşen fatura bulundu:', { outgoingInvoiceId, uuid: ettn });

      // HTML'i çek
      const htmlResponse = await accountingApi.post(ACCOUNTING_API.PAVOPAY_INTEGRATION_PROXY, {
        Path: '/api/EInvoice/InvoiceViewer/GetHTMLInvoice',
        QueryString: `id=${outgoingInvoiceId}`,
        Method: 'GET',
        BranchId: activePavoPayBranchId
      });

      const html = typeof htmlResponse.data === 'string' ? htmlResponse.data : JSON.stringify(htmlResponse.data);
      setPavoPayInvoiceHtml(html);
    } catch (err: any) {
      console.error('[PavoPay] Fatura HTML yüklenemedi:', err);
      setPavoPayInvoiceHtml(
        `<div style="padding:40px;text-align:center;font-family:sans-serif;">
          <p style="color:#d32f2f;font-size:16px;font-weight:600;">Aktif Bank üzerinden fatura görüntülenemedi</p>
          <p style="color:#666;margin-top:8px;">${err?.response?.data?.message || err?.message || 'Bilinmeyen hata'}</p>
          <p style="color:#999;margin-top:16px;font-size:13px;">Bu fatura sistem içi görüntülenebilir.</p>
        </div>`
      );
    } finally {
      setPavoPayViewerLoading(false);
    }
  };

  // EDM ile kesilen e-fatura/e-arşiv belgesinin HTML görünümünü backend üzerinden çek
  const handleViewEdmInvoice = async (invoice: Invoice) => {
    setEdmViewerOpen(true);
    setEdmViewerLoading(true);
    setEdmInvoiceHtml('');
    setEdmViewerError(null);
    setEdmViewerInvoiceRef(invoice.invoiceNumber || '');
    setEdmViewerInvoice(invoice);

    try {
      // Alış (gelen) e-faturalar EDM'de DB invoiceId ile değil, gelen kutusundan UUID ile gelir.
      const isIncoming = invoice.type === 'purchase';

      if (isIncoming) {
        const uuid = invoice.eInvoiceETTN || null;
        if (!uuid) {
          setEdmViewerError('Bu faturanın EDM belge anahtarı (UUID) bulunamadı.');
          return;
        }
        const response = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_CONTENT, {
          uuid,
          id: invoice.invoiceNumber || null,
          type: 'HTML',
          branchId: invoice.branchId || null,
        });
        const result = response.data;
        if (!result?.success || !result?.found || !result?.content) {
          setEdmViewerError('EDM gelen kutusunda bu faturanın görüntülenebilir belgesi bulunamadı.');
          return;
        }
        setEdmInvoiceHtml(result.content);
        return;
      }

      const response = await einvoiceApi.post(EINVOICE_API.EDM_DOCUMENT, {
        invoiceId: invoice.id,
        branchId: invoice.branchId || null,
      });
      const result = response.data;

      if (!result?.success) {
        setEdmViewerError(result?.error || 'Fatura belgesi alınamadı.');
        return;
      }

      if (!result.found || !result.html) {
        setEdmViewerError('Entegratör tarafında bu faturanın görüntülenebilir belgesi henüz hazır değil. Belge işleniyor olabilir, biraz sonra tekrar deneyin.');
        return;
      }

      setEdmInvoiceHtml(result.html);
    } catch (err: any) {
      console.error('[EDM] Fatura HTML yüklenemedi:', err);
      setEdmViewerError(handleApiError(err));
    } finally {
      setEdmViewerLoading(false);
    }
  };

  // EDM'den fatura belgesini XML (UBL) veya PDF olarak indir.
  // Giden (satış) → EDM_DOCUMENT_DOWNLOAD; gelen (alış) → EDM_INBOX_CONTENT (ikisi de XML/PDF döner).
  // invoiceOverride: görüntüleyici dışından (ör. liste "İndir" butonu) çağrı için.
  // opts.silent: hata durumunda alert göstermez (çağıran kağıt faturaya düşebilir).
  // Dönüş: indirme başarılıysa true.
  const handleDownloadEdmDocument = async (
    type: 'XML' | 'PDF' | 'HTML',
    invoiceOverride?: Invoice,
    opts?: { silent?: boolean }
  ): Promise<boolean> => {
    const invoice = invoiceOverride || edmViewerInvoice;
    if (!invoice) return false;
    const silent = !!opts?.silent;
    setEdmDownloading(type);
    try {
      // HTML zaten görüntüleyicide yüklü → sunucuya gitmeden doğrudan indir.
      // (Yalnızca görüntüleyiciden çağrıldığında; override'lı çağrıda yanlış içerik olmasın.)
      if (type === 'HTML' && edmInvoiceHtml && !invoiceOverride) {
        const blob = new Blob([edmInvoiceHtml], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(invoice.invoiceNumber || 'fatura')}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return true;
      }
      const isIncoming = invoice.type === 'purchase';
      let result: any;
      if (isIncoming) {
        const uuid = invoice.eInvoiceETTN || null;
        if (!uuid) {
          if (!silent) alert('Bu faturanın EDM belge anahtarı (UUID) bulunamadı.');
          return false;
        }
        const response = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_CONTENT, {
          uuid,
          id: invoice.invoiceNumber || null,
          type,
          branchId: invoice.branchId || null,
        });
        result = response.data;
      } else {
        const response = await einvoiceApi.post(EINVOICE_API.EDM_DOCUMENT_DOWNLOAD, {
          invoiceId: invoice.id,
          type,
          branchId: invoice.branchId || null,
        });
        result = response.data;
      }

      if (!result?.success || !result?.found || !result?.content) {
        if (!silent) alert(result?.error || 'EDM tarafında bu faturanın belgesi bulunamadı. Belge işleniyor olabilir, biraz sonra tekrar deneyin.');
        return false;
      }

      // İçeriği Blob'a çevir (XML/HTML → metin; PDF → base64)
      const fileName = result.fileName || `${invoice.invoiceNumber || 'fatura'}.${type.toLowerCase()}`;
      let blob: Blob;
      if (result.encoding === 'base64') {
        const byteChars = atob(result.content);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
        blob = new Blob([bytes], { type: result.contentType || 'application/pdf' });
      } else {
        blob = new Blob([result.content], { type: result.contentType || 'application/xml' });
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    } catch (err: any) {
      console.error('[EDM] Belge indirilemedi:', err);
      if (!silent) {
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.error;
        if (status === 404) {
          alert('İndirme servisi bulunamadı (404). E-fatura servisi (gts_einvoice_api) güncel kodla yeniden başlatılmalı.');
        } else {
          alert(serverMsg || handleApiError(err) || 'Belge indirilemedi.');
        }
      }
      return false;
    } finally {
      setEdmDownloading(null);
    }
  };

  // ✅ Akıllı indirme: e-belge olarak başarıyla kesilmiş faturada önce entegratördeki
  // RESMÎ belge (PDF) indirilir; alınamazsa (belge hazır değil / entegrasyon yok /
  // PavoPay gibi belge servisi olmayan sağlayıcı) kağıt fatura PDF'ine düşülür.
  const handleSmartDownload = async (invoice: Invoice) => {
    if (isEInvoiceSentSuccessfully(invoice)) {
      const ok = await handleDownloadEdmDocument('PDF', invoice, { silent: true });
      if (ok) return;
      console.warn('⚠️ [İNDİR] Resmî e-belge alınamadı, kağıt fatura PDF\'ine düşülüyor:', invoice.invoiceNumber);
    }
    handleDownloadPdf(invoice);
  };

  // EDM fatura HTML'ini yazdır (iframe veya yedek pencere).
  const handlePrintEdmInvoice = () => {
    if (!edmInvoiceHtml) return;

    const iframe = edmViewerIframeRef.current;
    if (iframe?.contentWindow) {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        return;
      } catch (err) {
        console.warn('[EDM] iframe yazdırma başarısız, yedek pencere deneniyor:', err);
      }
    }

    const printWin = window.open('', '_blank', 'noopener,noreferrer,width=900,height=900');
    if (!printWin) {
      alert(tt(
        'Yazdırma penceresi açılamadı. Tarayıcı pop-up engelleyicisini kontrol edin.',
        'Could not open print window. Check your browser pop-up blocker.'
      ));
      return;
    }
    printWin.document.open();
    printWin.document.write(edmInvoiceHtml);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => {
      printWin.print();
      printWin.onafterprint = () => printWin.close();
    }, 400);
  };

  // Hızlı satış faturası olup olmadığını kontrol et (notes alanından)
  const isQuickSaleInvoice = (invoice: Invoice): boolean => {
    const notes = invoice.notes?.toLowerCase() || '';
    return notes.includes('hızlı satış') || 
           notes.includes('pos satış') || 
           notes.includes('karma ödeme') ||
           invoice.type === 'quick_sale' ||
           invoice.transactionType === 'QUICK_SALE';
  };
  
  // ✅ YENİ: Hızlı satış faturası için ödeme tipini belirle
  const getQuickSalePaymentType = (invoice: Invoice): string => {
    const notes = invoice.notes?.toLowerCase() || '';
    if (notes.includes('karma ödeme')) return 'Karma Ödeme';
    if (notes.includes('pos satış')) return 'POS';
    if (notes.includes('hızlı satış (nakit)')) return 'Nakit';
    if (notes.includes('hızlı satış')) return 'Nakit';
    return 'Bilinmiyor';
  };

  const extractPavoPayInquiryLink = (invoice: Invoice): string | null => {
    const notes = invoice.notes || '';
    const match = notes.match(/PavoPay:\s*(https?:\/\/[^\s]+)/);
    return match ? match[1] : null;
  };

  // ✅ E-fatura olarak gönderilmiş mi kontrol et.
  // ETTN (QNB/eski akış) veya EDM akışının doldurduğu durum/belge no'ya bakar.
  // Not: "ERROR" durumu gönderim sayılmaz (yeniden denenebilmeli).
  const isEInvoiceSent = (invoice: Invoice): boolean => {
    if (invoice.eInvoiceETTN && invoice.eInvoiceETTN.trim() !== '') return true;
    const sentStatuses = ['SENT', 'APPROVED', 'REJECTED', 'CANCELED', 'CANCELLED'];
    if (invoice.eInvoiceStatus && sentStatuses.includes(invoice.eInvoiceStatus.trim().toUpperCase())) return true;
    if (invoice.eInvoiceBelgeOid && invoice.eInvoiceBelgeOid.trim() !== '') return true;
    return false;
  };

  // E-fatura gönderimi GİB/entegratör tarafında BAŞARISIZ olmuş mu?
  // "SEND - FAILED", "FAILED", "ERROR" gibi durumlarda UUID/ETTN atanmış olsa bile
  // ortada görüntülenebilir bir belge yoktur. Bu yüzden fatura "gönderildi" sayılmamalı:
  // kullanıcı yerel kalemleri görebilmeli, düzenleyebilmeli ve yeniden gönderebilmeli.
  const isEInvoiceFailed = (invoice: Invoice): boolean => {
    const s = (invoice.eInvoiceStatus || '').trim().toUpperCase();
    if (!s) return false;
    return s.includes('FAILED') || s.includes('ERROR') || s.includes('HATA');
  };

  // Başarıyla gönderilmiş (ve belge oluşmuş) e-fatura: FAILED/ERROR durumlarını hariç tutar.
  const isEInvoiceSentSuccessfully = (invoice: Invoice): boolean =>
    isEInvoiceSent(invoice) && !isEInvoiceFailed(invoice);

  // Entegratörde iptal edilmiş e-belge (e-arşiv iptali). Listede tik yerine çarpı gösterilir.
  const isEInvoiceCanceled = (invoice: Invoice): boolean =>
    ['CANCELED', 'CANCELLED'].includes((invoice.eInvoiceStatus || '').trim().toUpperCase());

  // Silme engeli: Başarıyla gönderilmiş e-faturalar silinemez.
  // İSTİSNA 1: PavoPay — belge cihaz/Pavo tarafında kesilir, bizdeki kayıt yansımadır.
  // Yerel kaydın silinmesi Pavo/GİB tarafındaki e-belgeyi etkilemez, bu yüzden silmeye izin verilir.
  // İSTİSNA 2: İptal edilmiş (CANCELED) e-arşiv — entegratörde hükümsüz; yerel kayıt ve
  // hareketleri silinerek muhasebe (stok/cari/kasa/banka) geri düzeltilebilir.
  const isDeleteBlockedEInvoice = (invoice: Invoice): boolean => {
    const status = (invoice.eInvoiceStatus || '').trim().toUpperCase();
    if (status === 'CANCELED' || status === 'CANCELLED') return false;
    return isEInvoiceSentSuccessfully(invoice)
      && (invoice.eInvoiceProvider || '').toLowerCase() !== 'pavopay';
  };

  // E-fatura sağlayıcısına göre tooltip metni ve kısa etiket (tik ikonu üzerinde gösterilir).
  const getEInvoiceProviderInfo = (provider?: string): { tooltip: string; tag: string | null; color: 'success.main' | 'info.main' } => {
    switch ((provider || '').toLowerCase()) {
      case 'pavopay':
        return { tooltip: 'PavoPay e-fatura/e-arşiv', tag: 'PP', color: 'info.main' };
      case 'edm':
        return { tooltip: 'EDM e-fatura/e-arşiv', tag: 'EDM', color: 'success.main' };
      case 'finansbank':
        return { tooltip: 'QNB Finansbank e-fatura', tag: null, color: 'success.main' };
      default:
        return { tooltip: 'E-fatura', tag: null, color: 'success.main' };
    }
  };

  // Faturayı düzenleme (type parametresi güncellendi)
  const handleEditInvoice = (invoiceToEdit: Invoice) => {
    // ✅ YENİ: Hızlı satış faturaları düzenlenemez kontrolü
    if (isQuickSaleInvoice(invoiceToEdit)) {
      // Düzenleme izin verilmiyor - menüde zaten disabled olacak
      console.warn('⚠️ [FATURA] Hızlı satış faturası düzenlenemez:', invoiceToEdit.invoiceNumber);
      setError(`Bu bir Hızlı Satış faturasıdır (${getQuickSalePaymentType(invoiceToEdit)} ile ödendi). Kapalı faturalar düzenlenemez.`);
      return;
    }

    // ✅ YENİ: E-fatura ile gelen/gönderilen faturalar düzenlenemez (hem satış hem alış)
    // Gönderimi başarısız (SEND - FAILED / ERROR) faturalar düzenlenebilir: kullanıcı düzeltip yeniden göndersin.
    if (isEInvoiceSentSuccessfully(invoiceToEdit)) {
      const invoiceTypeText = invoiceToEdit.type === 'sales' ? 'satış' : 'alış';
      const actionText = invoiceToEdit.type === 'sales' ? 'gönderilen' : 'gelen';
      console.warn('⚠️ [FATURA] E-fatura faturası düzenlenemez:', invoiceToEdit.invoiceNumber, invoiceToEdit.type);
      setError(`Bu fatura e-fatura sistemi üzerinden ${actionText} bir ${invoiceTypeText} faturasıdır ve düzenlenemez.\n\nFatura No: ${invoiceToEdit.invoiceNumber}\nETTN: ${invoiceToEdit.eInvoiceETTN}`);
      return;
    }

    // localStorage kullanımı kaldırıldı.
    const path = invoiceToEdit.type === 'purchase' ? 'alis' : 'satis';
    navigate(`/fatura/${path}/duzenle/${invoiceToEdit.id}`);
  };
  
  // Faturayı silme - Modal'ı aç
  const handleDeleteInvoice = (invoice: Invoice) => {
    setSelectedInvoiceForDelete(invoice);
    setIsDeleteModalOpen(true);
  };

  // ✅ E-Arşiv fatura iptali (yalnızca e-ARŞİV; e-fatura GİB kuralı gereği iptal edilemez).
  // 1) Entegratörde (EDM/Pavo) iptal edilir → GİB'e iptal kaydı raporlanır.
  // 2) Başarılıysa muhasebeyi düzeltmek için mevcut atomik silme onayı açılır
  //    (fatura + stok/cari/kasa/banka hareketleri geri alınır).
  const [cancelingEArchive, setCancelingEArchive] = useState(false);
  const [eArchiveCancelTarget, setEArchiveCancelTarget] = useState<Invoice | null>(null);

  // Menüden tıklanınca yalnızca onay modalını açar; asıl iptal confirmCancelEArchiveInvoice'ta.
  const handleCancelEArchiveInvoice = (invoice: Invoice) => {
    setEArchiveCancelTarget(invoice);
  };

  const confirmCancelEArchiveInvoice = async () => {
    const invoice = eArchiveCancelTarget;
    if (!invoice) return;

    setCancelingEArchive(true);
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_CANCEL, {
        invoiceId: invoice.id,
        branchId: invoice.branchId || null,
      });
      if (!resp.data?.success) {
        setError(resp.data?.error || resp.data?.message || 'E-Arşiv iptali başarısız oldu.');
        return;
      }

      console.log('✅ [E-ARŞİV İPTAL] Entegratörde iptal edildi:', invoice.invoiceNumber, resp.data);

      // Muhasebe temizliği: mevcut atomik silme akışı (onay modalıyla).
      // Durum yerelde CANCELED olarak güncellendi; modalda da bu bilgiyle açıyoruz.
      setSelectedInvoiceForDelete({ ...invoice, eInvoiceStatus: 'CANCELED' });
      setIsDeleteModalOpen(true);
    } catch (err: any) {
      console.error('❌ [E-ARŞİV İPTAL] Hata:', err);
      setError(err?.response?.data?.error || handleApiError(err) || 'E-Arşiv iptali sırasında hata oluştu.');
    } finally {
      setCancelingEArchive(false);
      setEArchiveCancelTarget(null);
      // Liste durumunu tazele (iptal işareti görünsün)
      if (companyId) loadInvoicesPage(companyId, page + 1, rowsPerPage, searchTerm, invoiceTypeFilter, eInvoiceProfileFilter, sortColumn, sortDirection, branchFilter);
    }
  };
  
  // E-Fatura gönder - Modal'ı aç
  const handleSendEInvoice = (invoice: Invoice) => {
    setSelectedInvoiceForEInvoice(invoice);
    setIsEInvoiceSendModalOpen(true);
  };

  // ✅ Gönderilmiş bir e-faturadan İADE faturası oluştur (orijinali ön-doldurur, KALAN miktarla)
  const handleCreateReturnInvoice = async (invoice: Invoice) => {
    try {
      // Tam kalemleri içeren faturayı çek
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(invoice.id));
      const data = response.data?.data || response.data;

      // ✅ İade özetini al (kalem bazında KALAN iade edilebilir miktar). Endpoint henüz
      // deploy edilmemişse (404) zarifçe orijinal miktarlara düşeriz.
      const remainingByKey: Record<string, number> = {};
      let summaryStatus: string | undefined;
      try {
        const sumResp = await accountingApi.get(ACCOUNTING_API.INVOICE_RETURN_SUMMARY(invoice.id));
        const summary = sumResp.data?.data || sumResp.data;
        summaryStatus = summary?.returnStatus;
        (summary?.items || []).forEach((s: any) => {
          const key = (s.itemId && !String(s.itemId).startsWith('manual_'))
            ? `id:${String(s.itemId).toLowerCase()}`
            : `name:${String(s.itemName || '').trim().toLowerCase()}`;
          remainingByKey[key] = s.remainingQuantity;
        });
      } catch (e) {
        console.warn('⚠️ [IADE] return-summary alınamadı, orijinal miktarlar kullanılacak (endpoint deploy edilmemiş olabilir).');
      }

      if (summaryStatus === 'FULL') {
        setError('Bu fatura zaten tamamen iade edilmiş. Yeni iade oluşturulamaz.');
        return;
      }

      const itemsWithRemaining = (data.items || [])
        .map((it: any) => {
          const key = (it.itemId && !String(it.itemId).startsWith('manual_'))
            ? `id:${String(it.itemId).toLowerCase()}`
            : `name:${String(it.itemName || '').trim().toLowerCase()}`;
          const remaining = (key in remainingByKey) ? remainingByKey[key] : it.quantity;
          return { it, remaining };
        })
        // Kalanı 0 olan kalemleri at (kısmi iade desteği)
        .filter(({ remaining }: { remaining: number }) => remaining > 0)
        .map(({ it, remaining }: { it: any; remaining: number }) => ({
          itemId: it.itemId,
          itemName: it.itemName,
          quantity: remaining,
          unit: it.unit,
          unitPrice: it.unitPrice,
          enteredPrice: it.enteredPrice,
          priceType: it.priceType,
          unitPriceWithVat: it.unitPriceWithVat,
          vatRate: it.vatRate,
          sctRate: it.sctRate,
          discountRate: it.discountRate,
          discountAmount: it.discountAmount,
          lineTotal: it.lineTotal,
          netTotal: it.netTotal,
          vatAmount: it.vatAmount,
          sctAmount: it.sctAmount,
          grossTotal: it.grossTotal,
          warehouseId: it.warehouseId,
        }));

      if (itemsWithRemaining.length === 0) {
        setError('İade edilebilecek kalan miktar bulunamadı (tüm kalemler iade edilmiş olabilir).');
        return;
      }

      const payload = {
        // ✅ İade ilişkisi — DB'ye kalıcılaştırma için
        returnedInvoiceId: data.id,
        returnedInvoiceNumber: data.invoiceNumber,
        returnedInvoiceDate: data.invoiceDate,
        currency: data.currency || 'TRY',
        accountId: data.accountId,
        accountName: data.accountName,
        accountTaxNumber: data.accountTaxNumber,
        accountTaxOffice: data.accountTaxOffice,
        accountAddress: data.accountAddress,
        accountPhone: data.accountPhone,
        accountEmail: data.accountEmail,
        // ✅ Tevkifat/Stopaj bilgileri — iade faturası orijinalle aynı kesintilere sahip olmalı
        //   (tevkifatlı/stopajlı faturadan TEVKIFATIADE üretebilmek için).
        tevkifatRate: data.tevkifatRate,
        tevkifatType: data.tevkifatType,
        stopajRate: data.stopajRate,
        items: itemsWithRemaining,
      };

      localStorage.setItem('returnFromInvoice', JSON.stringify(payload));
      // İade, orijinal faturanın tipine göre kesilir: alış faturasına alış-iadesi, satışa satış-iadesi.
      const returnPath = invoice.type === 'purchase' ? 'alis' : 'satis';
      navigate(`/fatura/${returnPath}/yeni?fromReturn=true`);
    } catch (err) {
      console.error('❌ [IADE] İade faturası oluşturma için kaynak fatura çekilemedi:', err);
      setError('İade faturası oluşturulamadı: kaynak fatura bilgileri alınamadı.');
    }
  };
  
  // E-Fatura durum sorgula
  const handleQueryEInvoiceStatus = (invoice: Invoice) => {
    console.log('🔍 [DURUM SORGULA] Modal açılıyor:', invoice);
    setSelectedInvoiceForStatusQuery(invoice);
    setIsEInvoiceStatusModalOpen(true);
  };
  
  // E-Fatura gönderimi başarılı olduğunda
  const handleEInvoiceSendSuccess = () => {
    console.log('✅ E-Fatura başarıyla gönderildi');
    // Fatura listesini yenile
    if (companyId) {
      loadAllInvoices(companyId);
    }
  };

  // Fatura silme onayı
  const handleConfirmDelete = async () => {
    if (!selectedInvoiceForDelete) return;
    
    // TODO: Silme işlemi backend'e gönderilecek
    console.log('Fatura siliniyor:', selectedInvoiceForDelete.id);
    
    // Modal'ı kapat
    setIsDeleteModalOpen(false);
    setSelectedInvoiceForDelete(null);
    
    // Fatura listesini yenile
    if (companyId) {
      await loadAllInvoices(companyId);
    }
  };
  
  // Para formatı - Para birimi sembolü sağda
  const formatCurrency = (amount: number | undefined, currencyCode: string): string => {
    if (typeof amount !== 'number') return '-'; // Başına kontrol eklendi
    
    // Sayıyı formatla (para birimi olmadan)
    const formattedNumber = new Intl.NumberFormat(localeCode, { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    
    // Para birimi sembolünü sağa ekle
    const currencySymbols: { [key: string]: string } = {
      'TRY': '₺',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    
    const symbol = currencySymbols[currencyCode] || currencyCode;
    return `${formattedNumber} ${symbol}`;
  };
  
  // Tarih formatı
  const formatDate = (dateString: string | undefined): string => { // Parametre tipi güncellendi
    if (!dateString) return '-';
    
    try {
      // Tarih formatını uygun şekilde ayarla
      const date = new Date(dateString as string);
      // Geçerli bir tarih mi kontrol et
      if (isNaN(date.getTime())) return '-';
      
      return new Intl.DateTimeFormat(localeCode, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return '-';
    }
  };
  
  // Sütun değerini elde et
  const getCellValue = (invoice: Invoice, columnId: string, rowIndex?: number): React.ReactNode => {
    switch(columnId) {
      case 'rowNumber':
        // Sayfalama ile birlikte sıra numarasını hesapla
        return rowIndex !== undefined ? (page * rowsPerPage) + rowIndex + 1 : '-';
      case 'invoiceNumber': 
        // E-fatura gönderilmişse entegratörün seriden ürettiği RESMİ numara öncelikli
        // gösterilir; iç numara (sistem no) altta küçük yazıyla kalır.
        if (invoice.eInvoiceOfficialNumber) {
          return (
            <Tooltip
              title={tt(
                `Resmi e-fatura no: ${invoice.eInvoiceOfficialNumber} | Sistem no: ${invoice.invoiceNumber}`,
                `Official e-invoice no: ${invoice.eInvoiceOfficialNumber} | System no: ${invoice.invoiceNumber}`
              )}
              arrow
            >
              <Box sx={{ maxWidth: 150 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {invoice.eInvoiceOfficialNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {invoice.invoiceNumber}
                </Typography>
              </Box>
            </Tooltip>
          );
        }
        return (
          <Tooltip title={invoice.invoiceNumber} arrow>
            <Typography variant="body2" sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {invoice.invoiceNumber}
            </Typography>
          </Tooltip>
        );
      case 'sourceOfferNumber':
        // ✅ Tekliften oluşturulmuşsa teklif numarasını göster
        return invoice.sourceOfferNumber || '-';
      case 'type':
        const invoiceTypeKey = invoice.transactionType || invoice.type;
        const typeIcon = (invoiceTypeKey === 'PURCHASE' || invoiceTypeKey === 'purchase') 
          ? <ShoppingCartIcon fontSize="small" /> 
          : (invoiceTypeKey === 'QUICK_SALE' || invoiceTypeKey === 'quick_sale')
          ? <PointOfSaleIcon fontSize="small" />
          : <ShoppingBagIcon fontSize="small" />;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
            <Chip 
              icon={typeIcon}
              label={getInvoiceDisplayType(invoice)}
              color={getInvoiceTypeColor(invoice.type)}
              size="small"
              variant="outlined"
            />
            {isReturnInvoice(invoice) && (
              <Tooltip title={invoice.returnedInvoiceNumber ? tt(`Orijinal fatura: ${invoice.returnedInvoiceNumber}`, `Original invoice: ${invoice.returnedInvoiceNumber}`) : ''}>
                <Chip
                  icon={<ReplayIcon fontSize="small" />}
                  label={getReturnLabel(invoice)}
                  color="warning"
                  size="small"
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      case 'eInvoiceProfileType':
        // E-Fatura profil türünü göster
        if (!invoice.eInvoiceProfileType) {
          return '-';
        }
        return (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getEInvoiceProfileTypeDisplay(invoice.eInvoiceProfileType)}
          </Typography>
        );
      case 'eInvoiceStatus':
        if (isEInvoiceCanceled(invoice)) {
          return (
            <Tooltip title={tt('Bu e-belge entegratör üzerinden İPTAL edilmiştir.', 'This e-document has been CANCELED via the integrator.')}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                <CancelIcon sx={{ color: 'error.main', fontSize: 24 }} />
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.65rem' }}>
                  {tt('İptal', 'Canceled')}
                </Typography>
              </Box>
            </Tooltip>
          );
        }
        if (isEInvoiceFailed(invoice)) {
          return (
            <Tooltip title={`E-fatura gönderimi başarısız (${invoice.eInvoiceStatus}). Faturayı düzenleyip yeniden gönderebilirsiniz.`}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ color: 'error.main', fontSize: 22 }} />
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.65rem' }}>
                  {tt('Başarısız', 'Failed')}
                </Typography>
              </Box>
            </Tooltip>
          );
        }
        if (isEInvoiceSent(invoice)) {
          const provInfo = getEInvoiceProviderInfo(invoice.eInvoiceProvider || activeEInvoiceProvider || undefined);
          return (
            <Tooltip title={provInfo.tooltip}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ color: provInfo.color, fontSize: 24 }} />
                {provInfo.tag && (
                  <Typography variant="caption" sx={{ color: provInfo.color, fontWeight: 600, fontSize: '0.65rem' }}>
                    {provInfo.tag}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        }
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">-</Typography>
          </Box>
        );
      case 'companyName': 
        return invoice.account?.companyName;
      case 'date': 
        return formatDate(invoice.date);
      case 'dueDate': 
        return formatDate(invoice.dueDate); // formatDate undefined kabul ediyor
      case 'currency': 
        return invoice.currency;
      case 'branchId':
        if (!invoice.branchId) return '-';
        return branchMap[invoice.branchId] || invoice.branchId;
      case 'grandTotal': 
        // ✅ Alış faturasında orijinal tutar varsa onu göster
        const displayTotal = (invoice.type === 'purchase' && invoice.originalInvoiceTotal) 
          ? invoice.originalInvoiceTotal 
          : invoice.grandTotal;
        return formatCurrency(displayTotal, invoice.currency || 'TRY');
      case 'status': 
        return (
          <Typography
            variant="body2"
            sx={{
              color: getStatusColor(invoice.status),
              fontWeight: 'bold'
            }}
          >
            {getStatusText(invoice.status)}
          </Typography>
        );
      case 'actions':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton 
              size="small"
              onClick={(e) => handleOpenMenu(e, invoice)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      default:
        return '-';
    }
  };
  
  // Aktif sütunların listesini oluştur
  const visibleColumns = tableColumns.filter(column => column.visible);

  // AccountingDataTable kolon tanımları (dinamik visibleColumns'dan üretilir)
  const invoiceColumns = useMemo<DataTableColumn<Invoice>[]>(() => {
    return visibleColumns.map((col): DataTableColumn<Invoice> => {
      const base: DataTableColumn<Invoice> = {
        id: col.id,
        label: col.label,
      };

      switch (col.id) {
        case 'rowNumber':
          return { ...base, width: '60px', render: (_row, index) => (
            <Box component="span" sx={{ color: 'text.secondary' }}>{page * rowsPerPage + index + 1}</Box>
          )};
        case 'companyName':
          // Proje bağı cari adının altında küçük etiket olarak gösterilir (PYS)
          return { ...base, field: 'companyName' as keyof Invoice, sortable: true, render: (row) => (
            <Box>
              <Box component="span">{decodeMojibake(row.account?.companyName) || '-'}</Box>
              <ProjectBadge projectName={row.projectName} />
            </Box>
          )};
        case 'date':
          return { ...base, field: 'date' as keyof Invoice, sortable: true, render: (row) => <>{formatDate(row.date)}</> };
        case 'type':
          return { ...base, field: 'type', sortable: true, render: (row) => {
            const invoiceTypeKey = row.transactionType || row.type;
            const typeIcon = (invoiceTypeKey === 'PURCHASE' || invoiceTypeKey === 'purchase')
              ? <ShoppingCartIcon fontSize="small" />
              : (invoiceTypeKey === 'QUICK_SALE' || invoiceTypeKey === 'quick_sale')
              ? <PointOfSaleIcon fontSize="small" />
              : <ShoppingBagIcon fontSize="small" />;
            const isReturn = isReturnInvoice(row);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                <Chip
                  icon={typeIcon}
                  label={getInvoiceDisplayType(row)}
                  color={getInvoiceTypeColor(row.type)}
                  size="small"
                  variant="outlined"
                />
                {isReturn && (
                  <Tooltip title={row.returnedInvoiceNumber ? tt(`Orijinal fatura: ${row.returnedInvoiceNumber}`, `Original invoice: ${row.returnedInvoiceNumber}`) : ''}>
                    <Chip
                      icon={<ReplayIcon fontSize="small" />}
                      label={getReturnLabel(row)}
                      color="warning"
                      size="small"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                  </Tooltip>
                )}
              </Box>
            );
          }};
        case 'eInvoiceProfileType':
          return { ...base, field: 'eInvoiceProfileType' as keyof Invoice, sortable: true, render: (row) => {
            if (!row.eInvoiceProfileType) return <>-</>;
            return (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {getEInvoiceProfileTypeDisplay(row.eInvoiceProfileType)}
              </Typography>
            );
          }};
        case 'invoiceNumber':
          // E-fatura gönderilmişse entegratördeki RESMİ numara üstte kalın gösterilir;
          // iç numara (sistem no) altta küçük yazıyla kalır (referans bütünlüğü için).
          return { ...base, field: 'invoiceNumber', sortable: true, tooltip: true, render: (row) => {
            if (row.eInvoiceOfficialNumber) {
              return (
                <Tooltip
                  title={tt(
                    `Resmi e-fatura no: ${row.eInvoiceOfficialNumber} | Sistem no: ${row.invoiceNumber}`,
                    `Official e-invoice no: ${row.eInvoiceOfficialNumber} | System no: ${row.invoiceNumber}`
                  )}
                  arrow
                >
                  <Box sx={{ maxWidth: 150 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.eInvoiceOfficialNumber}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.invoiceNumber}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            }
            return (
              <Typography variant="body2" sx={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.invoiceNumber}
              </Typography>
            );
          }};
        case 'sourceOfferNumber':
          return { ...base, field: 'sourceOfferNumber' as keyof Invoice, sortable: true, render: (row) => <>{row.sourceOfferNumber || '-'}</> };
        case 'dueDate':
          return { ...base, field: 'dueDate' as keyof Invoice, sortable: true, render: (row) => <>{formatDate(row.dueDate)}</> };
        case 'currency':
          return { ...base, field: 'currency', sortable: true };
        case 'eInvoiceStatus':
          return { ...base, align: 'center', render: (row) => {
            if (isEInvoiceCanceled(row)) {
              return (
                <Tooltip title={tt('Bu e-belge entegratör üzerinden İPTAL edilmiştir.', 'This e-document has been CANCELED via the integrator.')}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 24 }} />
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.65rem' }}>
                      {tt('İptal', 'Canceled')}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            }
            if (isEInvoiceFailed(row)) {
              return (
                <Tooltip title={`E-fatura gönderimi başarısız (${row.eInvoiceStatus}). Faturayı düzenleyip yeniden gönderebilirsiniz.`}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                    <WarningIcon sx={{ color: 'error.main', fontSize: 22 }} />
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.65rem' }}>
                      {tt('Başarısız', 'Failed')}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            }
            if (isEInvoiceSent(row)) {
              const provInfo = getEInvoiceProviderInfo(row.eInvoiceProvider || activeEInvoiceProvider || undefined);
              return (
                <Tooltip title={provInfo.tooltip}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon sx={{ color: provInfo.color, fontSize: 24 }} />
                    {provInfo.tag && (
                      <Typography variant="caption" sx={{ color: provInfo.color, fontWeight: 600, fontSize: '0.65rem' }}>
                        {provInfo.tag}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            }
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">-</Typography>
              </Box>
            );
          }};
        case 'branchId':
          return { ...base, field: 'branchId' as keyof Invoice, sortable: true, render: (row) => {
            if (!row.branchId) return <Typography variant="body2" color="text.secondary">-</Typography>;
            return (
              <Chip
                label={branchMap[row.branchId] || row.branchId}
                size="small"
                variant="outlined"
                color="default"
              />
            );
          }};
        case 'grandTotal':
          return { ...base, field: 'grandTotal', align: 'right', sortable: true, render: (row) => {
            const displayTotal = (row.type === 'purchase' && row.originalInvoiceTotal)
              ? row.originalInvoiceTotal
              : row.grandTotal;
            const currency = row.currency || 'TRY';
            return (
              <Typography
                variant="body2"
                fontWeight="bold"
                color="success.main"
                sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
              >
                {displayTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₺'}
              </Typography>
            );
          }};
        case 'status':
          return { ...base, field: 'status' as keyof Invoice, sortable: true, render: (row) => (
            <Typography variant="body2" sx={{ color: getStatusColor(row.status), fontWeight: 'bold' }}>
              {getStatusText(row.status)}
            </Typography>
          )};
        case 'actions':
          return { ...base, align: 'right', render: (row) => (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenMenu(e, row); }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          )};
        default:
          return { ...base, render: () => <>-</> };
      }
    });
  }, [visibleColumns, page, rowsPerPage, hasEInvoiceIntegration, branchMap, activeEInvoiceProvider]);
  
  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(tt('Faturalar', 'Invoices'));
      
      // Başlık satırı
      worksheet.mergeCells('A1:I1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = tt('Fatura Listesi', 'Invoice List');
      titleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      // Boş satır ekle
      worksheet.addRow([]);
      
      // Tablo başlıkları (E-Fatura sütunu sadece entegrasyon varsa)
      const headers = [
        tt('Fatura No', 'Invoice No'),
        tt('Tür', 'Type'),
        tt('Fatura Türü', 'Invoice Profile'),
        tt('Cari Hesap', 'Account'),
        tt('Fatura Tarihi', 'Invoice Date'),
        tt('Vade Tarihi', 'Due Date'),
        tt('Para Birimi', 'Currency'),
        tt('Toplam Tutar', 'Grand Total'),
        tt('Durum', 'Status'),
        tt('Açıklama', 'Description')
      ];
      
      if (hasEInvoiceIntegration) {
        // "Toplam Tutar"dan önce "E-Fatura" sütununu ekle
        headers.splice(7, 0, tt('E-Fatura', 'E-Invoice'));
      }
      
      const headerRow = worksheet.addRow(headers);
      
      // Başlık stili
      headerRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });
      
      // Fatura verilerini ekle (mevcut sayfadaki veriler)
      invoices.forEach((invoice) => {
        const rowData = [
          invoice.invoiceNumber,
          getInvoiceDisplayType(invoice),
          getEInvoiceProfileTypeDisplay(invoice.eInvoiceProfileType),
          invoice.account?.companyName,
          formatDate(invoice.date),
          formatDate(invoice.dueDate),
          invoice.currency,
          invoice.grandTotal,
          getStatusText(invoice.status),
          invoice.notes || '-'
        ];
        
        // E-Fatura sütununu ekle (sadece entegrasyon varsa)
        if (hasEInvoiceIntegration) {
          rowData.splice(7, 0, isEInvoiceSent(invoice) ? `✓ ${tt('Gönderildi', 'Sent')}` : '-');
        }
        
        worksheet.addRow(rowData);
      });
      
      // Sütun genişliklerini ayarla
      worksheet.getColumn(1).width = 15; // Fatura No
      worksheet.getColumn(2).width = 10; // Tür
      worksheet.getColumn(3).width = 15; // Fatura Türü (E-Fatura Profil)
      worksheet.getColumn(4).width = 30; // Cari Hesap
      worksheet.getColumn(5).width = 15; // Fatura Tarihi
      worksheet.getColumn(6).width = 15; // Vade Tarihi
      worksheet.getColumn(7).width = 10; // Para Birimi
      
      if (hasEInvoiceIntegration) {
        worksheet.getColumn(8).width = 12; // E-Fatura
        worksheet.getColumn(9).width = 15; // Toplam Tutar
        worksheet.getColumn(10).width = 15; // Durum
        worksheet.getColumn(11).width = 30; // Açıklama
      } else {
        worksheet.getColumn(8).width = 15; // Toplam Tutar
        worksheet.getColumn(9).width = 15; // Durum
        worksheet.getColumn(10).width = 30; // Açıklama
      }
      
      // Para birimi sütunu için stil
      if (hasEInvoiceIntegration) {
        worksheet.getColumn(9).numFmt = '#,##0.00'; // Toplam Tutar (E-Fatura sütunu varsa 9. sütun)
      } else {
        worksheet.getColumn(8).numFmt = '#,##0.00'; // Toplam Tutar (E-Fatura sütunu yoksa 8. sütun)
      }
      
      // Zebrastripe (alternatif satır renklendirme)
      for (let i = 4; i <= worksheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        worksheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Detay sayfası ekle
      const detailsWorksheet = workbook.addWorksheet(tt('Fatura Detayları', 'Invoice Details'));
      
      // Detay başlık satırı
      detailsWorksheet.mergeCells('A1:J1');
      const detailTitleCell = detailsWorksheet.getCell('A1');
      detailTitleCell.value = tt('Fatura Detayları', 'Invoice Details');
      detailTitleCell.style = {
        font: { bold: true, size: 14, color: { argb: 'FF000000' } },
        alignment: { horizontal: 'center' }
      };
      
      // Boş satır ekle
      detailsWorksheet.addRow([]);
      
      // Detay tablo başlıkları (E-Fatura sütunu sadece entegrasyon varsa)
      const detailHeaders = [
        tt('Fatura No', 'Invoice No'),
        tt('Tür', 'Type'),
        tt('Fatura Türü', 'Invoice Profile'),
        tt('Cari Hesap', 'Account'),
        tt('Fatura Tarihi', 'Invoice Date'),
        tt('Ürün Adı', 'Item Name'),
        tt('Birim', 'Unit'),
        tt('Miktar', 'Quantity'),
        tt('Birim Fiyat', 'Unit Price'),
        tt('KDV Oranı', 'VAT Rate'),
        tt('Toplam Tutar', 'Grand Total')
      ];
      
      if (hasEInvoiceIntegration) {
        // "Cari Hesap"tan sonra "E-Fatura" sütununu ekle
        detailHeaders.splice(4, 0, tt('E-Fatura', 'E-Invoice'));
      }
      
      const detailHeaderRow = detailsWorksheet.addRow(detailHeaders);
      
      // Detay başlık stili
      detailHeaderRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          }
        };
      });
      
      // Fatura detaylarını ekle (mevcut sayfadaki veriler)
      invoices.forEach((invoice) => {
        if (invoice.items && invoice.items.length > 0) {
          invoice.items.forEach((item: any) => {
            const detailRowData = [
              invoice.invoiceNumber,
              getInvoiceDisplayType(invoice),
              getEInvoiceProfileTypeDisplay(invoice.eInvoiceProfileType),
              invoice.account?.companyName,
              formatDate(invoice.date),
              item.itemName || '-',
              item.unit || 'Adet',
              item.quantity || 0,
              item.unitPrice || 0,
              item.vatRate ? `%${item.vatRate}` : '%0',
              item.grossTotal || 0
            ];
            
            // E-Fatura sütununu ekle (sadece entegrasyon varsa)
            if (hasEInvoiceIntegration) {
              detailRowData.splice(4, 0, isEInvoiceSent(invoice) ? '✓ Gönderildi' : '-');
            }
            
            detailsWorksheet.addRow(detailRowData);
          });
        }
      });
      
      // Detay sütun genişliklerini ayarla
      detailsWorksheet.getColumn(1).width = 15; // Fatura No
      detailsWorksheet.getColumn(2).width = 10; // Tür
      detailsWorksheet.getColumn(3).width = 15; // Fatura Türü (E-Fatura Profil)
      detailsWorksheet.getColumn(4).width = 30; // Cari Hesap
      
      if (hasEInvoiceIntegration) {
        detailsWorksheet.getColumn(5).width = 12; // E-Fatura
        detailsWorksheet.getColumn(6).width = 15; // Fatura Tarihi
        detailsWorksheet.getColumn(7).width = 30; // Ürün Adı
        detailsWorksheet.getColumn(8).width = 10; // Birim
        detailsWorksheet.getColumn(9).width = 10; // Miktar
        detailsWorksheet.getColumn(10).width = 15; // Birim Fiyat
        detailsWorksheet.getColumn(11).width = 10; // KDV Oranı
        detailsWorksheet.getColumn(12).width = 15; // Toplam Tutar
      } else {
        detailsWorksheet.getColumn(5).width = 15; // Fatura Tarihi
        detailsWorksheet.getColumn(6).width = 30; // Ürün Adı
        detailsWorksheet.getColumn(7).width = 10; // Birim
        detailsWorksheet.getColumn(8).width = 10; // Miktar
        detailsWorksheet.getColumn(9).width = 15; // Birim Fiyat
        detailsWorksheet.getColumn(10).width = 10; // KDV Oranı
        detailsWorksheet.getColumn(11).width = 15; // Toplam Tutar
      }
      
      // Sayısal formatlı sütunlar
      if (hasEInvoiceIntegration) {
        detailsWorksheet.getColumn(9).numFmt = '#,##0.00'; // Miktar
        detailsWorksheet.getColumn(10).numFmt = '#,##0.00'; // Birim Fiyat
        detailsWorksheet.getColumn(12).numFmt = '#,##0.00'; // Toplam Tutar
      } else {
        detailsWorksheet.getColumn(8).numFmt = '#,##0.00'; // Miktar
        detailsWorksheet.getColumn(9).numFmt = '#,##0.00'; // Birim Fiyat
        detailsWorksheet.getColumn(11).numFmt = '#,##0.00'; // Toplam Tutar
      }
      
      // Detay sayfası için zebrastripe
      for (let i = 4; i <= detailsWorksheet.rowCount; i++) {
        const fillColor = i % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
        detailsWorksheet.getRow(i).eachCell(cell => {
          cell.style = {
            ...cell.style,
            fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
            border: {
              top: { style: 'thin' as const },
              left: { style: 'thin' as const },
              bottom: { style: 'thin' as const },
              right: { style: 'thin' as const }
            }
          };
        });
      }
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${tt('Fatura_Listesi', 'Invoice_List')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      console.log('Excel dışa aktarma tamamlandı.');
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(tt('Excel dışa aktarma sırasında bir hata oluştu.', 'An error occurred during Excel export.'));
    } finally {
      setLoadingExport(false);
    }
  };

  // Yeni: Firma Bilgileri Modalı Açma/Kapatma
  const handleOpenCompanyDetailsModal = () => {
    // İdeal olarak burada admin kontrolü yapılabilir
    setIsCompanyDetailsModalOpen(true);
  };

  const handleCloseCompanyDetailsModal = (updated?: boolean) => {
    setIsCompanyDetailsModalOpen(false);
    // Eğer modal içinde bilgiler güncellendiyse, companyBranchInfo'yu tekrar yükle
    const uid = ownAuthSession.getCurrentUid();
    if (updated && uid) {
      console.log("Firma bilgileri güncellendi, bilgiler yeniden yükleniyor...");
      loadCompanyDetails(uid);
    }
  };

  // Faturayı PDF olarak indirmek için yeni bir fonksiyon ekleyelim - useEffect'lerden sonra
  // Faturayı PDF olarak indirme fonksiyonu
  const handleDownloadPdf = async (invoice: Invoice) => {
    setLoadingInvoiceDetail(true);
    setIsDisplayModalOpen(true);
    
    const fullInvoice = await fetchInvoiceWithItems(invoice);
    setSelectedInvoiceForDisplay(fullInvoice);
    setLoadingInvoiceDetail(false);
    
    // Modal açıldıktan sonra kısa bir bekleme ile PDF oluşturma işlemini gerçekleştir
    setTimeout(() => {
      const printableContent = document.getElementById('printableInvoiceContent');
      if (!printableContent) return;

      const pdfOptions = {
        margin: 10,
        filename: `Fatura_${invoice.invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      html2canvas(printableContent, { 
        scale: 2, 
        logging: false,
        useCORS: true,
        allowTaint: true
      }).then(canvas => {
        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(`Fatura_${invoice.invoiceNumber}.pdf`);
        
        // Modal'ı kapat (PDF indirildikten sonra)
        setTimeout(() => {
          setIsDisplayModalOpen(false);
          setSelectedInvoiceForDisplay(null);
        }, 500);
      });
    }, 1000); // Modal açılma ve içerik yüklenme süresine göre bekle
  };

  return (
    <>
      <GlobalStyles styles={printStyles} />
      
      {/* Üst Başlık */}
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        mb: 2
      }}>
        <ReceiptIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
        <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
          {tt('Fatura Yönetimi', 'Invoice Management')}
        </Typography>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={loading && invoices.length === 0} cardCount={2}>
        <Grid item xs={12} sm={6} md={6}>
          <StatsCardItem
            icon={<ShoppingCartIcon />}
            color={STATS_COLORS.green}
            title={`${invoiceStats.salesCount} ${tt('Fatura', 'Invoice')}`}
            subtitle={`${tt('Bu Ay Satış', 'Sales This Month')} · ${tt('bu sayfa', 'this page')} (${formatStatsCurrency(invoiceStats.salesTotal)})`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <StatsCardItem
            icon={<ShoppingBagIcon />}
            color={STATS_COLORS.blue}
            title={`${invoiceStats.purchaseCount} ${tt('Fatura', 'Invoice')}`}
            subtitle={`${tt('Bu Ay Alış', 'Purchases This Month')} · ${tt('bu sayfa', 'this page')} (${formatStatsCurrency(invoiceStats.purchaseTotal)})`}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı - Küçültüldü */}
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Fatura Ara...', 'Search Invoice...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* ✅ YENİ: Fatura Türü Filtresi */}
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>{tt('Fatura Türü', 'Invoice Type')}</InputLabel>
              <Select
                value={invoiceTypeFilter}
                onChange={(e: SelectChangeEvent) => setInvoiceTypeFilter(e.target.value as 'all' | 'sales' | 'purchase')}
                label={tt('Fatura Türü', 'Invoice Type')}
              >
                <SelectMenuItem value="all">{tt('Tümü', 'All')}</SelectMenuItem>
                <SelectMenuItem value="sales">{tt('Satış', 'Sales')}</SelectMenuItem>
                <SelectMenuItem value="purchase">{tt('Alış', 'Purchase')}</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* ✅ YENİ: E-Fatura Profil Türü Filtresi */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{tt('E-Fatura Türü', 'E-Invoice Type')}</InputLabel>
              <Select
                value={eInvoiceProfileFilter}
                onChange={(e: SelectChangeEvent) => setEInvoiceProfileFilter(e.target.value)}
                label={tt('E-Fatura Türü', 'E-Invoice Type')}
              >
                <SelectMenuItem value="all">{tt('Tümü', 'All')}</SelectMenuItem>
                {EINVOICE_PROFILE_TYPES.map(type => (
                  <SelectMenuItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Şube Filtresi */}
          <Grid item xs={12} md={2}>
            <Autocomplete
              size="small"
              options={[
                ...(isManager ? [] : [{ id: 'all', label: tt('Tümü', 'All') }, { id: 'none', label: tt('Şubesiz', 'No Branch') }]),
                ...Object.entries(branchMap).map(([id, name]) => ({ id, label: name }))
              ]}
              value={
                branchFilter === 'all' ? { id: 'all', label: tt('Tümü', 'All') } :
                branchFilter === 'none' ? { id: 'none', label: tt('Şubesiz', 'No Branch') } :
                branchMap[branchFilter] ? { id: branchFilter, label: branchMap[branchFilter] } : null
              }
              onChange={(_, newValue) => {
                setBranchFilter(newValue?.id || 'all');
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={isManager}
              disableClearable={isManager}
              renderInput={(params) => <TextField {...params} label={tt('Şube', 'Branch')} />}
              fullWidth
            />
          </Grid>

          {/* Desktop Butonlar - Aynı Satır */}
          <Grid item xs={12} md={isManager ? 2 : 4.5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              {!isManager && (
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<ShoppingBagIcon />}
                  onClick={handleNewSalesInvoice}
                >
                  {tt('Yeni Satış Faturası', 'New Sales Invoice')}
                </Button>
              )}
              {!isManager && (
                <Button 
                  variant="outlined" 
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleNewPurchaseInvoice}
                >
                  {tt('Yeni Alış Faturası', 'New Purchase Invoice')}
                </Button>
              )}
              <Button 
                variant="outlined"
                onClick={handleOpenMoreMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar ve Filtreler */}
          <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
            {/* Mobil Filtreler */}
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tt('Fatura Türü', 'Invoice Type')}</InputLabel>
                  <Select
                    value={invoiceTypeFilter}
                    onChange={(e: SelectChangeEvent) => setInvoiceTypeFilter(e.target.value as 'all' | 'sales' | 'purchase')}
                    label={tt('Fatura Türü', 'Invoice Type')}
                  >
                    <SelectMenuItem value="all">{tt('Tümü', 'All')}</SelectMenuItem>
                    <SelectMenuItem value="sales">{tt('Satış', 'Sales')}</SelectMenuItem>
                    <SelectMenuItem value="purchase">{tt('Alış', 'Purchase')}</SelectMenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tt('E-Fatura Türü', 'E-Invoice Type')}</InputLabel>
                  <Select
                    value={eInvoiceProfileFilter}
                    onChange={(e: SelectChangeEvent) => setEInvoiceProfileFilter(e.target.value)}
                    label={tt('E-Fatura Türü', 'E-Invoice Type')}
                  >
                    <SelectMenuItem value="all">{tt('Tümü', 'All')}</SelectMenuItem>
                    {EINVOICE_PROFILE_TYPES.map(type => (
                      <SelectMenuItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={[
                    ...(isManager ? [] : [{ id: 'all', label: tt('Tümü', 'All') }, { id: 'none', label: tt('Şubesiz', 'No Branch') }]),
                    ...Object.entries(branchMap).map(([id, name]) => ({ id, label: name }))
                  ]}
                  value={
                    branchFilter === 'all' ? { id: 'all', label: tt('Tümü', 'All') } :
                    branchFilter === 'none' ? { id: 'none', label: tt('Şubesiz', 'No Branch') } :
                    branchMap[branchFilter] ? { id: branchFilter, label: branchMap[branchFilter] } : null
                  }
                  onChange={(_, newValue) => {
                    setBranchFilter(newValue?.id || 'all');
                  }}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={isManager}
                  disableClearable={isManager}
                  renderInput={(params) => <TextField {...params} label={tt('Şube', 'Branch')} />}
                  fullWidth
                />
              </Grid>
            </Grid>
            
            {/* Mobil Butonlar */}
            <Grid container spacing={1}>
              {!isManager && (
                <Grid item xs={5}>
                  <Button 
                    fullWidth
                    size="small"
                    variant="contained" 
                    color="primary" 
                    startIcon={<ShoppingBagIcon />}
                    onClick={handleNewSalesInvoice}
                  >
                    {tt('Satış', 'Sales')}
                  </Button>
                </Grid>
              )}
              {!isManager && (
                <Grid item xs={5}>
                  <Button 
                    fullWidth
                    size="small"
                    variant="outlined" 
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleNewPurchaseInvoice}
                  >
                    {tt('Alış', 'Purchase')}
                  </Button>
                </Grid>
              )}
              <Grid item xs={isManager ? 12 : 2}>
                <Button 
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenMoreMenu}
                  size="small"
                  sx={{ minWidth: 'auto', px: 0.5 }}
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px' 
        }}>
          <CircularProgress />
        </Box>
      ) : invoices.length === 0 && totalCount === 0 ? (
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2
        }}>
          <ReceiptIcon 
            sx={{ 
              fontSize: 80, 
              color: 'primary.main',
              mb: 2
            }} 
          />
          
          <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
            {tt('Fatura Listesi', 'Invoice List')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm ? tt('Arama sonucu bulunamadı', 'No search results found') : tt('Henüz fatura kaydı bulunmamaktadır.', 'No invoice records yet.')}
          </Typography>
          
          {!isManager && (
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewSalesInvoice}
            >
              {tt('Yeni Satış Faturası Oluştur', 'Create New Sales Invoice')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          {/* Desktop: AccountingDataTable */}
          {!isMobile && (
            <>
              <AccountingDataTable<Invoice>
                columns={invoiceColumns}
                rows={invoices}
                getRowId={(row) => row.id}
                loading={loading}
                pagination={false}
                defaultSortField="date"
                defaultSortDirection="desc"
                onRowClick={handleViewInvoice}
                emptyMessage={tt('Henüz fatura kaydı bulunmamaktadır.', 'No invoice records yet.')}
                emptyIcon={<ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
                ariaLabel={tt('faturalar tablosu', 'invoices table')}
                onSort={handleSort}
                sortField={sortColumn}
                sortDirection={sortDirection}
                columnResizeStorageKey="invoices"
              />
              {/* Server-side Pagination (Desktop) */}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
                labelDisplayedRows={({ from, to, count }) =>
                  isTr ? `${count} kayıttan ${from}–${to} arası` : `${from}–${to} of ${count}`
                }
              />
            </>
          )}
          
          {/* Mobile: Card View */}
          {isMobile && (
            <Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {invoices.map((invoice, index) => (
                  <Card 
                    key={invoice.id}
                    sx={{ 
                      border: '1px solid',
                      borderColor: invoice.type === 'sales' ? 'primary.light' : invoice.type === 'purchase' ? 'secondary.light' : 'info.light',
                      bgcolor: invoice.type === 'sales' ? 'rgba(25, 118, 210, 0.04)' : invoice.type === 'purchase' ? 'rgba(156, 39, 176, 0.04)' : 'rgba(2, 136, 209, 0.04)'
                    }}
                  >
                    <CardContent>
                      {/* Başlık - Sıra No, Fatura No ve Tür */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={`#${(page * rowsPerPage) + index + 1}`}
                            size="small"
                            sx={{ 
                              minWidth: '40px',
                              fontWeight: 'bold',
                              bgcolor: 'grey.200'
                            }}
                          />
                          <ReceiptIcon color="primary" sx={{ fontSize: 20 }} />
                          {invoice.eInvoiceOfficialNumber ? (
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {invoice.eInvoiceOfficialNumber}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {invoice.invoiceNumber}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="subtitle1" fontWeight="bold">
                              {invoice.invoiceNumber}
                            </Typography>
                          )}
                        </Box>
                        <Chip 
                          label={getInvoiceDisplayType(invoice)}
                          color={getInvoiceTypeColor(invoice.type)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      
                      {/* Cari Hesap */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            {tt('Cari Hesap:', 'Account:')}
                        </Typography>
                        <Typography variant="body2">
                          {decodeMojibake(invoice.account?.companyName) || '-'}
                        </Typography>
                      </Box>
                      
                      {/* E-Fatura Türü */}
                      {invoice.eInvoiceProfileType && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {tt('Fatura Türü:', 'Invoice Profile:')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {getEInvoiceProfileTypeDisplay(invoice.eInvoiceProfileType)}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* E-Fatura Durumu (Entegrasyon varsa) */}
                      {hasEInvoiceIntegration && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {tt('E-Fatura:', 'E-Invoice:')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isEInvoiceCanceled(invoice) ? (
                              <>
                                <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                                  {tt('İptal edildi', 'Canceled')}
                                </Typography>
                              </>
                            ) : isEInvoiceFailed(invoice) ? (
                              <>
                                <WarningIcon sx={{ color: 'error.main', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                                  {tt('Gönderim başarısız', 'Send failed')} ({invoice.eInvoiceStatus})
                                </Typography>
                              </>
                            ) : isEInvoiceSent(invoice) ? (
                              <>
                                <CheckCircleIcon sx={{ color: invoice.eInvoiceProvider === 'pavopay' ? 'info.main' : 'success.main', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, color: invoice.eInvoiceProvider === 'pavopay' ? 'info.main' : 'success.main' }}>
                                  {invoice.eInvoiceProvider === 'pavopay' ? 'PavoPay' : tt('Gönderildi', 'Sent')}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {tt('Gönderilmedi', 'Not Sent')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Fatura ve Vade Tarihi */}
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {tt('Fatura Tarihi:', 'Invoice Date:')}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(invoice.date)}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {tt('Vade Tarihi:', 'Due Date:')}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(invoice.dueDate)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Toplam Tutar ve Para Birimi */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2, 
                        pt: 1.5, 
                        borderTop: '1px solid', 
                        borderColor: 'divider' 
                      }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {tt('Durum:', 'Status:')}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: getStatusColor(invoice.status),
                              fontWeight: 'bold'
                            }}
                          >
                            {getStatusText(invoice.status)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">
                            {tt('Toplam:', 'Total:')}
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {formatCurrency(
                              (invoice.type === 'purchase' && invoice.originalInvoiceTotal) 
                                ? invoice.originalInvoiceTotal 
                                : invoice.grandTotal,
                              invoice.currency || 'TRY'
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                      <IconButton 
                        size="small"
                        onClick={(e) => handleOpenMenu(e, invoice)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                ))}
              </Box>
              
              {/* Mobile Pagination */}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
                labelDisplayedRows={({ from, to, count }) =>
                  isTr ? `${count} kayıttan ${from}–${to} arası` : `${from}–${to} of ${count}`
                }
              />
            </Box>
          )}
        </>
      )}
      
      {/* Üst Menü - 3 Nokta Menüsü */}
      <Menu
        anchorEl={anchorElMore}
        open={Boolean(anchorElMore)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <SelectMenuItem 
          onClick={() => {
            exportToExcel();
            handleCloseMoreMenu();
          }}
          disabled={loadingExport || loading || invoices.length === 0}
        >
          <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />
          {loadingExport ? tt('Hazırlanıyor...', 'Preparing...') : tt('Excel İndir', 'Download Excel')}
        </SelectMenuItem>
        <SelectMenuItem onClick={handleOpenReports}>
          <AssessmentIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Raporlar', 'Reports')}
        </SelectMenuItem>
        {!isManager && (
          <>
            <Divider />
            <SelectMenuItem onClick={() => {
              handleOpenCompanyDetailsModal();
              handleCloseMoreMenu();
            }}>
              <BusinessIcon fontSize="small" sx={{ mr: 1 }} />
              {tt('Firma Bilgileri', 'Company Details')}
            </SelectMenuItem>
          </>
        )}
      </Menu>

      {/* Mobile Action Menu - Fatura Satırları */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <SelectMenuItem onClick={() => {
          if (selectedInvoice) handleViewInvoice(selectedInvoice);
          handleCloseMenu();
        }}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Görüntüle', 'View')}
        </SelectMenuItem>
        <Tooltip 
          title={
            selectedInvoice && isQuickSaleInvoice(selectedInvoice) 
              ? `Bu bir Hızlı Satış faturasıdır (${getQuickSalePaymentType(selectedInvoice)} ile ödendi). Kapalı faturalar düzenlenemez.` 
              : selectedInvoice && isEInvoiceSentSuccessfully(selectedInvoice)
              ? selectedInvoice.type === 'sales' 
                ? 'E-fatura olarak gönderilen satış faturaları düzenlenemez.'
                : 'E-fatura ile gelen alış faturaları düzenlenemez.'
              : ''
          }
          placement="left"
        >
          <span>
            <SelectMenuItem 
              onClick={() => {
                if (selectedInvoice) handleEditInvoice(selectedInvoice);
                handleCloseMenu();
              }}
              disabled={
                selectedInvoice 
                  ? (isQuickSaleInvoice(selectedInvoice) || isEInvoiceSentSuccessfully(selectedInvoice))
                  : false
              }
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              {selectedInvoice && (isQuickSaleInvoice(selectedInvoice) || isEInvoiceSentSuccessfully(selectedInvoice))
                ? tt('Düzenlenemez', 'Not Editable')
                : tt('Düzenle', 'Edit')
              }
            </SelectMenuItem>
          </span>
        </Tooltip>
        {/* Durum Sorgula: entegratörde belge oluşmuşsa (ETTN/UUID/BelgeOid veya gönderilmiş
            durum) HER durumda göster — yerel durum yanlışlıkla "başarısız" görünse bile
            gerçek durum entegratörden çekilip düzeltilir. Yeniden göndermeden önce mutlaka
            durum sorgulanabilmeli; aksi halde EDM'de mükerrer belge oluşabilir. */}
        {hasEInvoiceIntegration && selectedInvoice
          && (selectedInvoice.type === 'sales' || (selectedInvoice.type === 'purchase' && isReturnInvoice(selectedInvoice)))
          && isEInvoiceSent(selectedInvoice) ? (
          <SelectMenuItem onClick={() => {
            if (selectedInvoice) handleQueryEInvoiceStatus(selectedInvoice);
            handleCloseMenu();
          }}>
            <SearchIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
            <Typography color="info.main">{tt('Durum Sorgula', 'Query Status')}</Typography>
          </SelectMenuItem>
        ) : null}
        {/* E-Fatura Gönder / Yeniden Gönder: başarıyla gönderilmemiş faturalarda.
            Başarısız görünen faturada önce "Durum Sorgula" önerilir (yukarıda). */}
        {hasEInvoiceIntegration && selectedInvoice && (
          (selectedInvoice.type === 'sales' && !isEInvoiceSentSuccessfully(selectedInvoice))
          || (selectedInvoice.type === 'purchase' && isReturnInvoice(selectedInvoice) && !isEInvoiceSentSuccessfully(selectedInvoice))
        ) ? (
          <SelectMenuItem onClick={() => {
            if (selectedInvoice) handleSendEInvoice(selectedInvoice);
            handleCloseMenu();
          }}>
            <SendIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
            <Typography color="success.main">
              {selectedInvoice && isEInvoiceFailed(selectedInvoice)
                ? tt('E-Fatura Yeniden Gönder', 'Resend E-Invoice')
                : tt('E-Fatura Gönder', 'Send E-Invoice')}
            </Typography>
          </SelectMenuItem>
        ) : null}
        {/* İade Faturası Oluştur (iade faturasının kendisine ÇIKMAZ):
            - ALIŞ: e-faturadan içe aktarılmış alış faturaları → tedarikçiye iade e-faturası
            - SATIŞ: SADECE EDM TEST ortamında (edmIsTestMode) ve BAŞARIYLA gönderilmiş
                     satış e-faturaları → orijinal satış e-faturasına satış-iadesi
                     (cac:BillingReference ile). EDM canlıya alınınca otomatik gizlenir. */}
        {hasEInvoiceIntegration && selectedInvoice && (
          (selectedInvoice.type === 'purchase' && isEInvoiceSent(selectedInvoice) && !isReturnInvoice(selectedInvoice))
          || (
            selectedInvoice.type === 'sales'
            && edmIsTestMode
            && (activeEInvoiceProvider === 'edm' || (selectedInvoice.eInvoiceProvider || '').toLowerCase() === 'edm')
            && isEInvoiceSentSuccessfully(selectedInvoice)
            && !isReturnInvoice(selectedInvoice)
          )
        ) ? (
          <SelectMenuItem onClick={() => {
            if (selectedInvoice) handleCreateReturnInvoice(selectedInvoice);
            handleCloseMenu();
          }}>
            <ReplayIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
            <Typography color="warning.main">{tt('İade Faturası Oluştur', 'Create Return Invoice')}</Typography>
          </SelectMenuItem>
        ) : null}
        {/* E-Arşiv İptal — yalnızca başarıyla gönderilmiş, henüz iptal edilmemiş e-ARŞİV
            satış faturaları. E-Fatura (Temel/Ticari) GİB kuralı gereği iptal edilemez:
            ticari → alıcı reddi, temel → iade faturası / harici itiraz. */}
        {hasEInvoiceIntegration && selectedInvoice
          && selectedInvoice.type === 'sales'
          && isEInvoiceSentSuccessfully(selectedInvoice)
          && String(selectedInvoice.eInvoiceProfileType || '').toUpperCase().includes('ARSIV')
          && !['CANCELED', 'CANCELLED'].includes((selectedInvoice.eInvoiceStatus || '').toUpperCase()) ? (
          <SelectMenuItem
            disabled={cancelingEArchive}
            onClick={() => {
              if (selectedInvoice) handleCancelEArchiveInvoice(selectedInvoice);
              handleCloseMenu();
            }}
          >
            <CancelIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            <Typography color="error.main">
              {cancelingEArchive ? tt('İptal ediliyor...', 'Canceling...') : tt('E-Arşiv Faturayı İptal Et', 'Cancel E-Archive Invoice')}
            </Typography>
          </SelectMenuItem>
        ) : null}
        <Tooltip 
          title={
            selectedInvoice && isDeleteBlockedEInvoice(selectedInvoice)
              ? 'Bu bir e-faturadır, silinemez.'
              : ''
          }
          placement="left"
        >
          <span>
            <SelectMenuItem 
              onClick={() => {
                if (selectedInvoice) handleDeleteInvoice(selectedInvoice);
                handleCloseMenu();
              }}
              disabled={selectedInvoice ? isDeleteBlockedEInvoice(selectedInvoice) : false}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              {selectedInvoice && isDeleteBlockedEInvoice(selectedInvoice)
                ? tt('Silinemez', 'Cannot Delete')
                : tt('Sil', 'Delete')
              }
            </SelectMenuItem>
          </span>
        </Tooltip>
        <SelectMenuItem onClick={async () => {
          if (selectedInvoice) {
            setLoadingInvoiceDetail(true);
            setIsDisplayModalOpen(true);
            const fullInvoice = await fetchInvoiceWithItems(selectedInvoice);
            setSelectedInvoiceForDisplay(fullInvoice);
            setLoadingInvoiceDetail(false);
            setTimeout(() => window.print(), 500);
          }
          handleCloseMenu();
        }}>
          <PrintIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('Yazdır', 'Print')}
        </SelectMenuItem>
        <SelectMenuItem onClick={() => {
          // E-belge kesilmişse resmî belge (entegratör PDF'i), değilse kağıt fatura PDF'i.
          if (selectedInvoice) handleSmartDownload(selectedInvoice);
          handleCloseMenu();
        }}>
          <GetAppIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('İndir', 'Download')}
        </SelectMenuItem>
        <SelectMenuItem onClick={() => {
          if (selectedInvoice) {
            setHistoryModalInvoice({
              id: String(selectedInvoice.id),
              label: `${tt('Fatura', 'Invoice')}: ${selectedInvoice.invoiceNumber || selectedInvoice.id}`,
            });
          }
          handleCloseMenu();
        }}>
          <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
          {tt('İşlem Geçmişi', 'Activity History')}
        </SelectMenuItem>
      </Menu>

      {/* İşlem Geçmişi (Entity Audit Trail) — kalem değişiklikleri parent eşleşmesiyle aynı listede */}
      <RecordHistoryModal
        open={Boolean(historyModalInvoice)}
        onClose={() => setHistoryModalInvoice(null)}
        entityType="Invoice"
        entityId={historyModalInvoice?.id ?? null}
        recordLabel={historyModalInvoice?.label}
        fieldLabels={INVOICE_HISTORY_FIELD_LABELS}
        entityTypeLabels={{ InvoiceItem: tt('Fatura Kalemi', 'Invoice Line') }}
      />

      {(selectedInvoiceForDisplay || loadingInvoiceDetail) && (
        <InvoiceDisplayModal
          open={isDisplayModalOpen}
          onClose={handleCloseDisplayModal}
          invoice={selectedInvoiceForDisplay}
          companyId={companyId}
          loading={loadingInvoiceDetail}
          extraHeaderContent={
            selectedInvoiceForDisplay?.eInvoiceProvider === 'pavopay' ? (
              <>
                <Chip label="PavoPay" size="small" color="info" sx={{ color: 'white', fontWeight: 600 }} />
                {extractPavoPayInquiryLink(selectedInvoiceForDisplay) && (
                  <Tooltip title="PavoPay fatura sorgulama sayfasını aç">
                    <IconButton
                      size="small"
                      sx={{ color: 'white' }}
                      onClick={() => window.open(extractPavoPayInquiryLink(selectedInvoiceForDisplay)!, '_blank')}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : undefined
          }
        />
      )}

      {/* E-Fatura Uyarı Modalı */}
      <Dialog 
        open={showEInvoiceModal} 
        onClose={() => setShowEInvoiceModal(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <InfoIcon />
            <Typography variant="h6">E-Fatura Kullanın</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 80, color: 'info.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Lütfen E-Fatura Modülünü Kullanınız
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Türkiye'de e-fatura uygulaması zorunludur. 
              Yeni satış faturası kesmek için lütfen E-Fatura modülünü kullanın.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setShowEInvoiceModal(false)} 
            variant="outlined"
            color="inherit"
          >
            İptal
          </Button>
          <Button 
            onClick={() => {
              setShowEInvoiceModal(false);
              // E-Fatura modülüne yönlendir - path'i kontrol et
              navigate('/e-fatura'); 
            }} 
            variant="contained" 
            color="primary"
          >
            E-Fatura Modülüne Git
          </Button>
        </DialogActions>
      </Dialog>

      {/* Firma Bilgileri Modalı */}
      <CompanyDetailsModal
        open={isCompanyDetailsModalOpen}
        onClose={handleCloseCompanyDetailsModal}
        companyId={companyId}
      />

      {/* Fatura Silme Onay Modalı */}
      {selectedInvoiceForDelete && (
        <InvoiceDeleteConfirmModal
          open={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedInvoiceForDelete(null);
          }}
          onConfirmDelete={handleConfirmDelete}
          invoiceId={selectedInvoiceForDelete.id}
          companyId={companyId} // ✅ YENİ: Çok şirketli sistem için ZORUNLU
          extraWarning={
            ['CANCELED', 'CANCELLED'].includes((selectedInvoiceForDelete.eInvoiceStatus || '').toUpperCase())
              ? 'Bu e-arşiv faturası entegratörde İPTAL edilmiştir. Silme işlemi yerel kaydı ve tüm hareketlerini (stok, cari, kasa/banka) geri alarak muhasebeyi düzeltir.'
              : (selectedInvoiceForDelete.eInvoiceProvider || '').toLowerCase() === 'pavopay'
                && isEInvoiceSentSuccessfully(selectedInvoiceForDelete)
              ? 'Bu fatura PavoPay üzerinden e-belge olarak kesilmiştir. Silme işlemi yalnızca sistemdeki yerel kaydı ve hareketlerini kaldırır; Pavo/GİB tarafındaki e-belgeyi İPTAL ETMEZ.'
              : undefined
          }
        />
      )}

      {/* E-Arşiv Fatura İptal Onay Modalı */}
      <Dialog
        open={!!eArchiveCancelTarget}
        onClose={() => { if (!cancelingEArchive) setEArchiveCancelTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon color="error" />
          E-Arşiv Faturayı İptal Et
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            "{eArchiveCancelTarget?.invoiceNumber}" numaralı e-Arşiv faturası entegratör
            üzerinden <strong>İPTAL</strong> edilecek. Bu işlem geri alınamaz.
          </Alert>
          <Typography component="div" variant="body2">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>İptal kaydı GİB'e raporlanır ve geri alınamaz.</li>
              <li>Aynı fatura numarası bir daha kullanılamaz; gerekiyorsa yeni numarayla yeni fatura kesilir.</li>
              <li>Faturayı alıcıya ilettiyseniz iptali kendisine bildirmeniz önerilir.</li>
              <li>İptal sonrası, muhasebeyi düzeltmek için fatura ve tüm hareketlerini (stok, cari, kasa/banka) silme onayı açılacak.</li>
            </ul>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEArchiveCancelTarget(null)} disabled={cancelingEArchive}>
            Vazgeç
          </Button>
          <Button
            onClick={confirmCancelEArchiveInvoice}
            color="error"
            variant="contained"
            disabled={cancelingEArchive}
            startIcon={cancelingEArchive ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
          >
            {cancelingEArchive ? 'İptal Ediliyor...' : 'Faturayı İptal Et'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* E-Fatura Gönder Modalı */}
      {selectedInvoiceForEInvoice && (
        <EInvoiceSendModal
          open={isEInvoiceSendModalOpen}
          onClose={() => {
            setIsEInvoiceSendModalOpen(false);
            setSelectedInvoiceForEInvoice(null);
          }}
          invoiceId={selectedInvoiceForEInvoice.id}
          companyId={companyId}
          onSuccess={handleEInvoiceSendSuccess}
        />
      )}

      {/* E-Fatura Durum Sorgula Modalı */}
      {selectedInvoiceForStatusQuery && companyId && (
        <EInvoiceStatusModal
          open={isEInvoiceStatusModalOpen}
          onClose={() => {
            console.log('🔍 [DURUM SORGULA] Modal kapanıyor');
            setIsEInvoiceStatusModalOpen(false);
            setSelectedInvoiceForStatusQuery(null);
          }}
          invoiceId={selectedInvoiceForStatusQuery?.id || null}
          companyId={companyId}
          onStatusUpdated={({ invoiceId: updatedId, status, officialNumber }) => {
            // Sorgu backend'de faturanın durumunu güncelledi; listedeki satırı da
            // sayfa yenilemeye gerek kalmadan aynı duruma çek (başarısız ünlemi kalksın).
            setInvoices(prev => prev.map(inv => inv.id === updatedId
              ? {
                  ...inv,
                  eInvoiceStatus: status,
                  ...(officialNumber ? { eInvoiceOfficialNumber: officialNumber } : {}),
                }
              : inv));
          }}
        />
      )}

      {/* E-Fatura Detay Modalı */}
      {selectedEInvoiceETTN && (
        <EInvoiceDetailModal
          open={isEInvoiceDetailModalOpen}
          onClose={handleCloseEInvoiceDetailModal}
          ettn={selectedEInvoiceETTN}
          companyId={companyId}
          invoiceNumber={selectedEInvoiceNumber || undefined}
          invoiceType={selectedEInvoiceType || undefined}
        />
      )}

      {/* PavoPay Fatura Görüntüleme Modalı */}
      <Dialog
        open={pavoPayViewerOpen}
        onClose={() => { setPavoPayViewerOpen(false); setPavoPayInvoiceHtml(''); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="PavoPay" size="small" color="info" />
            <Typography variant="subtitle1" fontWeight={600}>
              {pavoPayViewerInvoiceRef ? `Fatura: ${pavoPayViewerInvoiceRef}` : 'Fatura Önizleme'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => { setPavoPayViewerOpen(false); setPavoPayInvoiceHtml(''); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
          {pavoPayViewerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{tt("Loading invoice from Aktif Bank...", "Loading invoice from Aktif Bank...")}</Typography>
            </Box>
          ) : (
            <iframe
              srcDoc={pavoPayInvoiceHtml}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="PavoPay Fatura Önizleme"
              sandbox="allow-same-origin allow-scripts"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* EDM Fatura Görüntüleme Modalı (kesilen e-fatura/e-arşiv HTML belgesi) */}
      <Dialog
        open={edmViewerOpen}
        onClose={() => { setEdmViewerOpen(false); setEdmInvoiceHtml(''); setEdmViewerError(null); setEdmViewerInvoice(null); }}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '900px',
            maxWidth: '95vw',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(edmViewerInvoice?.eInvoiceProvider || '').toLowerCase() === 'pavopay' ? (
              <Chip label="PavoPay" size="small" color="info" />
            ) : (
              <Chip label="EDM" size="small" color="success" />
            )}
            {edmViewerInvoice && isEInvoiceCanceled(edmViewerInvoice) && (
              <Chip label={tt('İPTAL EDİLDİ', 'CANCELED')} size="small" color="error" icon={<CancelIcon />} />
            )}
            <Typography variant="subtitle1" fontWeight={600}>
              {edmViewerInvoiceRef ? `Fatura: ${edmViewerInvoiceRef}` : 'Fatura Görüntüleme'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => { setEdmViewerOpen(false); setEdmInvoiceHtml(''); setEdmViewerError(null); setEdmViewerInvoice(null); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
          {edmViewerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{tt('Fatura entegratörden yükleniyor...', 'Loading invoice from provider...')}</Typography>
            </Box>
          ) : edmViewerError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4, textAlign: 'center' }}>
              <WarningIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />
              <Typography color="text.secondary">{edmViewerError}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {edmViewerInvoice && isEInvoiceCanceled(edmViewerInvoice) && (
                <Alert severity="error" icon={<CancelIcon />} sx={{ borderRadius: 0, fontWeight: 600 }}>
                  {tt(
                    'Bu belge entegratör üzerinden İPTAL edilmiştir ve hükümsüzdür. Yasal saklama yükümlülüğü gereği arşiv amaçlı görüntülenmektedir.',
                    'This document has been CANCELED via the integrator and is void. It is displayed for archival purposes due to legal retention requirements.'
                  )}
                </Alert>
              )}
              <iframe
                ref={edmViewerIframeRef}
                srcDoc={edmInvoiceHtml}
                style={{ width: '100%', flex: 1, border: 'none' }}
                title="EDM Fatura Görüntüleme"
                sandbox="allow-same-origin allow-scripts allow-modals"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!edmViewerInvoice || edmViewerLoading || !edmInvoiceHtml || edmDownloading !== null}
            startIcon={<PrintIcon fontSize="small" />}
            onClick={handlePrintEdmInvoice}
          >
            {tt('Yazdır', 'Print')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!edmViewerInvoice || edmViewerLoading || !edmInvoiceHtml || edmDownloading !== null}
            startIcon={edmDownloading === 'HTML' ? <CircularProgress size={16} /> : undefined}
            onClick={() => handleDownloadEdmDocument('HTML')}
          >
            {tt('HTML İndir', 'Download HTML')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!edmViewerInvoice || edmViewerLoading || edmDownloading !== null}
            startIcon={edmDownloading === 'XML' ? <CircularProgress size={16} /> : undefined}
            onClick={() => handleDownloadEdmDocument('XML')}
          >
            {tt('XML (UBL) İndir', 'Download XML (UBL)')}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disabled={!edmViewerInvoice || edmViewerLoading || edmDownloading !== null}
            startIcon={edmDownloading === 'PDF' ? <CircularProgress size={16} /> : undefined}
            onClick={() => handleDownloadEdmDocument('PDF')}
          >
            {tt('PDF İndir', 'Download PDF')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PavoPay Şube Seçim Uyarı Modalı */}
      <Dialog
        open={pavoPayBranchWarningOpen}
        onClose={() => setPavoPayBranchWarningOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>{tt('Şube Seçimi Gerekli', 'Branch Selection Required')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {tt(
              'PavoPay entegrasyon bilgisi şubeye ayarlıdır. Faturayı görüntüleyebilmek için lütfen önce üst kısımdaki şube filtresinden ilgili şubeyi seçin.',
              'PavoPay integration is configured per branch. To view the invoice, first select the related branch from the branch filter above.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPavoPayBranchWarningOpen(false)} variant="contained" size="small">
            {tt('Tamam', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default Invoice; 